import {
    HOURS,
    getDateIndex,
    getNumberOfDaysBetween,
    getCycleCounts,
    type IValidation,
    MINUTES,
    type ICycle,
    type IDay,
    msToTimespan,
    type IWorkday,
    type IDaySummary,
    type IWorkdaySummary,
    type ICycleSummary,
    type IDeferralInfo,
    onDutyStatuses,
    offDutyStatuses,
    type IState,
    type IRuleSetDateRange,
    type ICalculationResult,
    getBeginningOfDay,
    DAYS,
    type IRestInfo,
    doPeriodsOverlap,
    getPeriodOverlap,
    parseDateTime,
    getBeginningOfNextDay,
    getTimeLeftInDay,
    getCleanViolation,
    type IRawViolation,
    type ICurrentViolationMap,
    type IPeriod,
    timeSpanToMs,
    type TWorkdayResetInfo,
    type IOffDutySplitPeriod,
    type ISbSplitPeriod,
    TSplitPeriodType,
    ICustomCodeState
} from "./helpers";
import { getIntermediateSplitConfig } from "./intermediateSplitConfig";
import { closeCycleResetPeriodOnReturnToDuty, getDateRestBecameLongEnough, recordCycleResetPeriod } from "./cycleResetPeriod";

import {
    getDailyDrivingAvailabilityForDay,
    getDailyDrivingLimit,
    getDailyDutyAvailabilityForDay,
    getDailyDutyLimit,
    getRemainingDailyOffDutyRequired,
    getRemainingDailyRestRequired,
    getTotalDailyOffDutyRequired,
    getTotalDailyRestRequired
} from "./availabilityHelpers";
import {
    adverseExtensionAmount,
    applyDrivingExtensions,
    applyDutyExtensions,
    applyWorkdayExtensions,
    calculateExemptionExtensions,
    getEligibleDurationToReduceRest,
    isBigDayExemptionReset,
    isExemptionAppliedDuringWorkday
} from "./exemptionHelpers";
import { discreteAvailabilityFunctions, getDiscreteAvailabilitySummary } from "./availabilityDiscrete";
import {
    clearEmergencyResponseSuppressedViolation,
    closeViolationsInterruptedByEmergencyResponse,
    collectEmergencyResponseLogs,
    getNonExemptEmergencyResponseRanges,
    initialiseEmergencyResponse,
    isDailyViolationExemptedByEmergencyResponse,
    isEmergencyResponseActive,
    prunedEmergencyResponseLogsFor,
    tryRecordEmergencyResponseAdjustedViolation
} from "./exemptions/emergencyResponse";
import { calculateAvailability } from "./availabilityUI";
import { getNextHosEventDate } from "./nextHosEventCalculator";
import type { THosStatus, TViolationType } from "../interfaces/statues";
import type { IHosRuleset, IHosRulesetConfig } from "../interfaces/hosRuleset";
import type { IUserHosRuleSet } from "../interfaces/userHosRuleSet";
import DutyStatusIntervalTracker from "../utils/dutyStatusIntervalTracker";
import { computeNonSplitResult, type IDrivingInterval } from "./nonSplitProjection";
import { getNextAvailabilityEventDates } from "./nextAvailabilityEvents";
import { evaluateSplit, checkSplitPeriodsDuringWorkdayReset, createSplitSB, getRemainingSplitDuration, hasPartialSplitForCurrentWorkdaySymmetric, unfreezePriorSplitPeriods } from "./splitSb";

const MAX_DAY_ITERATION_THRESHOLD = 1e5; // 100k days ~= 274 years
const railroadWorkdayExtensionAmount = 3 * HOURS;
const railroadCycleExtensionAmount = 6 * HOURS;

// Initialize DutyStatusIntervalTracker for use throughout the file
let dutyStatusTracker: DutyStatusIntervalTracker;

export const 
    shouldCarryWorkdayOffDuty = (state: IState): boolean => {
    if (state.day.deferralStatus !== "DayOne" && state.day.deferralStatus !== "DayTwo") {
        return false;
    }
    return (
        !!state.config.workday?.offDutyMinimum &&
        state.config.workday?.offDutyMinimum !== state.workday.offDutyMinimum &&
        state.workday.offDutyMinimum !== 0
    );
};

export const getWorkdayOffNeeded = (state: IState) => {
    return state.workday.offDutyMinimum ? Math.max(0, state.workday.offDutyMinimum - state.workday.consecutiveOffCount) : 0;
};

export const getConfigWorkdayMaximum = (state: IState): number | undefined => {
    if (!state.config.workday) {
        return undefined;
    }
    if (state.config.workday.workdayMaximumWhenSplitSB && hasPartialSplitForCurrentWorkdaySymmetric(state)) {
        return state.config.workday.workdayMaximumWhenSplitSB;
    }
    if (
        state.config.workday.workdayMaximumWhenSplitSB &&
        !state.workday.completedSplitSB &&
        state.workday.splitSB &&
        state.workday.splitSB.periods.length > 0
    ) {
        {
            return state.config.workday.workdayMaximumWhenSplitSB;
        }
    }
    return state.config.workday.workdayMaximum;
};

export const isWorkdayReadyForReset = (state: IState): boolean => {
    if (!state.workday.offDutyMinimum) {
        return false;
    }
    return state.workday.consecutiveOffCount >= state.workday.offDutyMinimum;
};

export const getWorkdayForDateTime = (state: IState, dateTime: Date): IWorkday | undefined => {
    let workday: IWorkday | undefined;
    if (state.workdays.length > 0) {
        // iterating backwards to find the workday which contains the dateTime
        for (let i = state.workdays.length - 1; i >= 0; i--) {
            let wd = state.workdays[i];
            if (wd.start && wd.start.getTime() <= dateTime.getTime()) {
                workday = wd;
                break;
            }
        }
    }
    return workday;
};

let getDailyDeferralExemptions = (
        toLocalTime: (date: Date) => Date,
        otherLogs: any[],
        dayNoneLogEnabled: boolean,
        prevDeferralLogs: { [index: string]: any } | undefined = undefined
    ): { [index: string]: any } => {
        let deferralLogs: { [index: string]: any } = prevDeferralLogs || {};
        let offDutyDeferralLogs = otherLogs.filter(log => log.status === "ExemptionOffDutyDeferral"),
            exemptionLog,
            index: string;
        for (exemptionLog of offDutyDeferralLogs) {
            index = getDateIndex(exemptionLog.dateTime, toLocalTime);
            if (!dayNoneLogEnabled) {
                if (!deferralLogs[index]) {
                    deferralLogs[index] = exemptionLog;
                }
            } else if (!deferralLogs[index]) {
                deferralLogs[index] = {
                    deferralStatus: exemptionLog.deferralStatus,
                    deferralMinutes: exemptionLog.deferralMinutes
                };
            } else if (
                (deferralLogs[index].deferralStatus === "None" && exemptionLog.deferralStatus === "DayOne") ||
                (deferralLogs[index].deferralStatus === "DayOne" && exemptionLog.deferralStatus === "None")
            ) {
                deferralLogs[index] = {
                    deferralStatus: "DayOne",
                    requiredDeferralLog: "None",
                    deferralMinutes: Math.max(exemptionLog.deferralMinutes, deferralLogs[index].deferralMinutes)
                };
            }
        }
        return deferralLogs;
    },
    startNewCycle = (state: IState, currentDateTime: Date) => {
        if (state.cycle.fromDate.getTime() === currentDateTime.getTime()) {
            return;
        }

        if (!state.cycle.hasStarted) {
            state.cycles.pop();
        }
        state.cycle = {
            fromDate: new Date(currentDateTime.getTime()),
            toDate: new Date(currentDateTime.getTime() + (state.config.cycle?.cycleDays || 0) * DAYS),
            dutyCountSince24HourRestPeriod: 0,
            cycleConsecutiveOffCount: 0,
            driveCount: 0,
            onDutyCount: 0,
            days: 1,
            bigDayCount: 0,
            hasStarted: false,
            pauseCountingCycleOff: false,
            completedRestPeriodDates: [],
            cycleMaximum: 0
        };
        if (state.config.cycle && state.config.cycle.cycleRestMinimum) {
            state.cycle.completedRestPeriodDates.push(new Date(currentDateTime.getTime()));
        }
        if (state.cycles.length > 0) {
            state.cycles[state.cycles.length - 1].toDate = state.cycle.fromDate;
        }
        state.cycles.push(state.cycle);

        if (state.config.cycle && state.config.cycle.cycleMaximum) {
            state.cycle.cycleMaximum = state.config.cycle.cycleMaximum;
        }
    },
    resetCycleForWindowShift = (state: IState) => {
        if (state.config.cycle && state.config.cycle.cycleDays && state.cycle.days > state.config.cycle.cycleDays) {
            if (!state.config.cycle.nonRolling) {
                let dayOutsideOfCycleWindow = state.days[state.days.length - state.config.cycle.cycleDays - 1];
                if (dayOutsideOfCycleWindow) {
                    state.cycle.driveCount -= dayOutsideOfCycleWindow.driveCount;
                    state.cycle.onDutyCount -= dayOutsideOfCycleWindow.onDutyCount;
                    if (dayOutsideOfCycleWindow.bigDayExemptionUsed) {
                        state.cycle.bigDayCount -= 1;
                    }
                    if (dayOutsideOfCycleWindow.isRailroadLogApplied) {
                        state.cycle.cycleMaximum -= railroadCycleExtensionAmount;
                    }
                }
                state.cycle.days -= 1;
            }
        }
    },
    tryStartNewDay = (state: IState, currentDateTime: Date, dateIndex: string, isStartOfDay = false) => {
        if (state.day.dateIndex === dateIndex) {
            return;
        }
        let dayStart = isStartOfDay ? currentDateTime : getBeginningOfDay(currentDateTime, state.toLocalTime, state.toUtcTime);
        if (state.day.date >= dayStart) {
            return;
        }
        let yesterday = state.day;
        state.day = {
            dateIndex: dateIndex,
            date: dayStart,
            driveCount: 0,
            onDutyCount: 0,
            offDutyCount: 0,
            statusCounts: {},
            hoursInCycle: 0
        };
        state.days.push(state.day);
        state.cycle.days += 1;
        resetCycleForWindowShift(state);
        updateDayWithConfigValues(state);
        updateDayDeferral(state, yesterday);
        if (yesterday.deferralStatus === "DayTwo") {
            updateWorkdayOffDutyMinimum(state, undefined);
        }
    },
    updateDayWithConfigValues = (state: IState) => {
        if (!state.config.daily) {
            return;
        }
        if (state.config.daily.offDutyMinimum) {
            state.day.consecutiveOff = 0;
            state.day.unCountedOff = 0;
        }
        if (state.config.rest && state.config.rest.dailyRestMinimum) {
            state.day.restCount = 0;
            state.day.restInfo = [];
        }
    },
    updateDayDeferral = (state: IState, yesterday: IDay) => {
        if (!state.config.daily || yesterday.deferralStatus !== "DayOne" || yesterday.deferralTime === undefined) {
            return;
        }
        let deferralTime = yesterday.deferralTime;
        if (yesterday.offDutyCount > 0) {
            // CanELD 4.6.4 Test step 4, Test 1 - step p)
            // if the driver deferred more than they needed to, adjust that amount (Ex. 9:00h(taken)-8:50h(needed) => adjust by 10m)
            const extraDeferred = Math.max(0, yesterday.offDutyCount - (getTotalDailyOffDutyRequired(yesterday, state.config) ?? 0));
            if (deferralTime > extraDeferred) {
                deferralTime -= extraDeferred;
            }
        }
        // MYG-31570 CanadaEldTestStep4Test1 alert at step p)
        // updateWorkdayOffDutyMinimum uses state.day.deferralTime, it must be not be > than time needed
        if (!state.customCodes.enableAutoCreateNoneDeferralLog) {
            state.day.deferralLogNeeded = true;
        } else {
            state.day.deferralLogRequired = "DayTwo";
        }
        state.day.deferralStatus = "DayTwo";
        state.day.deferralTime = deferralTime;
        // DayTwo
        state.day.yesterdayDriveCount = yesterday.driveCount;
    },
    // eslint-disable-next-line complexity
    canadianDutyDeferral = (state: IState, dateIndex: string) => {
        // Canadian duty deferral (disabled for Oil Well Service Vehicle Permits)
        if (state.config.daily?.deferralAllowed === false) {
            return;
        }

        let deferralLog = state.deferralLogs[dateIndex];
        let yesterday = state.days[state.days.length - 2];

        if (state.customCodes.enableAutoCreateNoneDeferralLog && state.day.deferralStatus !== "None") {
            let isDayNoneRequiredButNotPresent =
                (!deferralLog || (deferralLog && deferralLog.requiredDeferralLog !== "None" && deferralLog.deferralStatus !== "None")) &&
                yesterday?.deferralStatus === "DayTwo" &&
                !!yesterday?.deferralTime;
            let isDayNone = deferralLog?.deferralStatus === "None" || isDayNoneRequiredButNotPresent;
            if (isDayNoneRequiredButNotPresent && state.day.deferralStatus === undefined) {
                state.day.deferralLogRequired = "None";
            }
            let isDayOne = (deferralLog && deferralLog?.deferralStatus === "DayOne") || state.day.deferralStatus === "DayOne";
            if (isDayNone && !isDayOne) {
                if (!isDayOne) {
                    state.day.deferralStatus = "None";
                    state.day.deferralTime = 0;
                }
                delete state.deferralLogs[dateIndex];
            }
        }

        if (!deferralLog) {
            return;
        }

        let yesterdayDeferralAmount = yesterday && yesterday.deferralStatus === "DayOne" ? yesterday.deferralTime : undefined;
        if (yesterdayDeferralAmount !== undefined) {
            // DayTwo
            if (deferralLog) {
                if (state.day.deferralTime === undefined) {
                    state.day.deferralTime = deferralLog.deferralMinutes * MINUTES;
                }
                state.day.deferralStatus = "DayTwo";
                if (!state.customCodes.enableAutoCreateNoneDeferralLog) {
                    state.day.deferralLogNeeded = false;
                } else {
                    delete state.day.deferralLogRequired;
                }
            } else {
                state.day.deferralTime = yesterdayDeferralAmount;
                state.day.deferralStatus = "DayTwo";
                if (!state.customCodes.enableAutoCreateNoneDeferralLog) {
                    state.day.deferralLogNeeded = true;
                } else {
                    state.day.deferralLogRequired = "DayTwo";
                }
            }
        } else {
            let deferralTime = deferralLog.deferralMinutes * MINUTES;
            if (deferralLog.deferralStatus === "DayOne") {
                // DayOne
                state.day.deferralTime = deferralTime;
                if (!state.customCodes.enableAutoCreateNoneDeferralLog) {
                    state.day.deferralLogNeeded = false;
                }
                // Prevent removing deferralLogRequired if a DayOne log is present after DayTwo, but a DayNone log is missing.
                else if (state.day.deferralStatus !== undefined) {
                    delete state.day.deferralLogRequired;
                }
                state.day.deferralStatus = "DayOne";
            }
        }
        delete state.deferralLogs[dateIndex];
    },
    applyReducedRestAndCalculateWorkdayResetInfo = (state: IState) => {
        // ReducedRest exemption allows resetting the workday with >4 but <8h off-duty.Reset workday with at least 4 hours on DayOne. Reduced rest on DayOne added to the next shift reset requirement.
        let maxOffDutyMin = state.config.workday?.offDutyMinimum;
        if (!state.config.workday?.exemptionReducedRest || !state.workday.hasStarted || state.reducedRestLogs.length === 0 || !maxOffDutyMin) {
            return;
        }

        // Only need to consider the next reduced rest log and only when we have past it's datetime. This ensures we are counting all eligible rest to reduce from the total.
        let reducedRestLog = state.reducedRestLogs[0];

        // Process if exemption is within or before current log's duration (using state.nextDate like other functions do) todo: should this be >= ?
        if (reducedRestLog.dateTime > state.nextDate) {
            return;
        }

        // should only consider the latest eligible reduced rest log. If there are multiple in the past then cull the oldest.
        if (state.reducedRestLogs.length >= 2) {
            let firstIndexAfterNow = state.reducedRestLogs.findIndex((log: { dateTime: Date }) => log.dateTime > state.nextDate);
            if (firstIndexAfterNow >= 1) {
                state.reducedRestLogs.splice(0, firstIndexAfterNow);
            }
        }

        let { restAmountToBeDeferred, nextWorkdayStart } = getEligibleDurationToReduceRest(state, reducedRestLog.dateTime);
        if (restAmountToBeDeferred <= 0) {
            return;
        }

        let resetInfo: TWorkdayResetInfo | undefined = undefined;
        if (nextWorkdayStart && state.workdays.length > 0 && state.config.workday?.exemptionReducedRest) {
            // Calculate the base resetInfo using current state
            const baseResetInfo = calculateOffDutyMinimumResetInfo(state.workday, nextWorkdayStart);
            if (baseResetInfo && baseResetInfo.type === "offDutyMinimum") {
                // Use the configured reduced rest minimum (e.g., 4h) for eligibleForResetDate. This is when the driver becomes eligible for reset, not when the period actually ends
                const configuredReducedMinimum = state.config.workday.exemptionReducedRest.offDutyMinimum;
                resetInfo = {
                    ...baseResetInfo,
                    eligibleForResetDate: new Date(baseResetInfo.fromDate.getTime() + configuredReducedMinimum)
                };
            } else {
                resetInfo = baseResetInfo;
            }
        }

        state.reducedRestLogs.shift();
        state.workday.isExemptionReducedRestApplied = true;
        state.workday.prevLastReducedRestApplyDate = state.lastReducedRestApplyDate;
        unfreezePriorSplitPeriods(state);
        state.lastReducedRestApplyDate = reducedRestLog.dateTime;
        if (state.workday.offDutyMinimum) {
            state.workday.offDutyMinimum -= restAmountToBeDeferred;
        }
        state.workday.reducedRestUsed = restAmountToBeDeferred / MINUTES;
        if (nextWorkdayStart) {
            // Starting new workday. Removing old logs is not necessary but an optimization since split will not need to calculation violations before this date.
            let dateLimit = nextWorkdayStart;
            state.logs = state.logs.filter(l => !dateLimit || l.dateTime >= dateLimit);
            state.i = 0;
            state.next = state.logs[0];
            startNewWorkday(state, nextWorkdayStart, false, state.workday.isAdverseLogApplied, resetInfo);
        }
    },
    calculateOffDutyMinimumResetInfo = (workday: IWorkday, startDate: Date): TWorkdayResetInfo | undefined => {
        // calculate off-duty minimum reset period from consecutive off-duty time
        const consecutiveOffTime = workday.consecutiveOffCount || 0;
        const offDutyMin = workday.offDutyMinimum || 0;

        // exit early if we don't have the necessary data
        if (consecutiveOffTime <= 0 || offDutyMin <= 0) {
            return undefined;
        }

        // Use the tracked consecutive off start time if available, otherwise fall back to calculated time
        const fromDate = workday.consecutiveOffStartTime || new Date(startDate.getTime() - consecutiveOffTime);
        const eligibleForResetDate = new Date(fromDate.getTime() + offDutyMin);

        return {
            type: "offDutyMinimum",
            fromDate,
            eligibleForResetDate,
            toDate: startDate
        };
    },
    startNewWorkday = (state: IState, startDate: Date, hasStarted: boolean, reApplyAdverseLogForSB: boolean | undefined, workdayResetInfo?: TWorkdayResetInfo) => {
        let drivingExtended: number | undefined;
        let drivingLimit: number | undefined;
        let dutyLimit: number | undefined;
        let workdayExtended: number | undefined;
        let offDutyMinimum: number | undefined;
        let reducedRestUsed: number | undefined;
        let earnedSplitExclusion: number | undefined;
        let offDutySplitPeriodFromStandardWorkdayReset: IWorkday["offDutySplitPeriodFromStandardWorkdayReset"] =
            state.workday.completedSplitSB ? undefined : state.workday.offDutySplitPeriodFromStandardWorkdayReset;
        let splitEligibleConsecutiveOffStartTime: Date | undefined;
        let consecutiveSleeperBerthCount: number | undefined;
        let longestSleeperBerthInConsecutiveOff: number | undefined;
        let carriedResetPairingSleeperStart: Date | undefined;
        let resetInfoBeforeCarriedResetPairing: TWorkdayResetInfo | undefined;
        let replacedPreviousWorkdayResetInfoForCarriedPairing: boolean | undefined;
        let consecutiveOffCount = 0;
        let consecutiveWtCount = 0;
        let isSplitting = state.workday.completedSplitSB;
        if (reApplyAdverseLogForSB && state.workday.adverseLogStart && startDate < state.workday.adverseLogStart) {
            drivingExtended = state.workday.drivingExtended;
            workdayExtended = state.workday.workdayExtended;
        }
        if (state.config.workday?.exemptionReducedRest) {
            let wasExtendedReducedRestResetJustCompleted =
                state.workday.hasStarted && state.workday.reducedRestUsed && !state.workday.isExemptionReducedRestApplied;
            reducedRestUsed = wasExtendedReducedRestResetJustCompleted ? undefined : state.workday.reducedRestUsed;
        }
        if (!state.workday.hasStarted) {
            state.workdays.pop();
            if (state.workday.drivingExtended !== undefined) {
                drivingExtended = state.workday.drivingExtended;
                drivingLimit = state.workday.drivingLimit;
                dutyLimit = state.workday.dutyLimit;
            }
            if (state.workday.workdayExtended !== undefined) {
                workdayExtended = state.workday.workdayExtended;
            }
            // The exclusion earned by the completed pairing belongs to the workday being started,
            // not to the pre-start placeholder it was recorded on — carry it forward. MYG-133117.
            earnedSplitExclusion = state.workday.earnedSplitExclusion;
            if (state.config.railroadExempt && state.config.cycle?.cycleMaximum && state.config.cycle.cycleMaximum !== state.cycle.cycleMaximum) {
                workdayExtended = (workdayExtended || 0) + railroadWorkdayExtensionAmount;
            }
            //compare config workday off duty min and state.workday.offDutyMinimum if diff then carry
            if (shouldCarryWorkdayOffDuty(state)) {
                offDutyMinimum = state.workday.offDutyMinimum;
            }
            consecutiveOffCount = state.workday.consecutiveOffCount;
            consecutiveWtCount = state.workday.consecutiveWtCount;
            splitEligibleConsecutiveOffStartTime = state.workday.splitEligibleConsecutiveOffStartTime;
            consecutiveSleeperBerthCount = state.workday.consecutiveSleeperBerthCount;
            longestSleeperBerthInConsecutiveOff = state.workday.longestSleeperBerthInConsecutiveOff;
            carriedResetPairingSleeperStart = state.workday.carriedResetPairingSleeperStart;
            resetInfoBeforeCarriedResetPairing = state.workday.resetInfoBeforeCarriedResetPairing;
            replacedPreviousWorkdayResetInfoForCarriedPairing = state.workday.replacedPreviousWorkdayResetInfoForCarriedPairing;
        } else if (!isSplitting) {
            // carry over consecutive off time. used to calculate rest breaks.
            consecutiveOffCount = state.workday.consecutiveOffCount;
            consecutiveWtCount = state.workday.consecutiveWtCount;
            splitEligibleConsecutiveOffStartTime = state.workday.splitEligibleConsecutiveOffStartTime;
            consecutiveSleeperBerthCount = state.workday.consecutiveSleeperBerthCount;
            longestSleeperBerthInConsecutiveOff = state.workday.longestSleeperBerthInConsecutiveOff;
            // A sleeper paired with the carried reset candidate can still be running when its rest resets the
            // workday; the stamped summary on the earlier workday has to keep following it.
            carriedResetPairingSleeperStart = state.workday.carriedResetPairingSleeperStart;
            resetInfoBeforeCarriedResetPairing = state.workday.resetInfoBeforeCarriedResetPairing;
            replacedPreviousWorkdayResetInfoForCarriedPairing = state.workday.replacedPreviousWorkdayResetInfoForCarriedPairing;
        }

        // cleanup split log collection.
        delete state.workday.splitLogs;

        // end on-going workday violations
        completeViolations(state, "Workday", state.currentDate);
        completeViolations(state, "Driving", state.currentDate);
        completeViolations(state, "Duty", state.currentDate);
        completeViolations(state, "Rest", state.currentDate);

        const previousWorkday = state.workdays.length > 0 ? state.workdays[state.workdays.length - 1] : undefined;
        if (previousWorkday && workdayResetInfo && !previousWorkday.resetInfo) {
            previousWorkday.resetInfo = workdayResetInfo;
        }

        const initSplitConfig = state.config.splitConfig;
        // completedSplitSB tracks classic "property" only (excludes californiaProperty, which sets onlyFreezeBigPeriods).
        const isClassicProperty = initSplitConfig?.type === "asymmetric" && !initSplitConfig.onlyFreezeBigPeriods;
        state.workday = {
            hasStarted: hasStarted,
            consecutiveOffCount: consecutiveOffCount,
            consecutiveWtCount: consecutiveWtCount,
            drivingExtended: drivingExtended,
            workdayExtended: workdayExtended,
            endOfLastBreak: startDate,
            completedSplitSB: isClassicProperty ? isSplitting : false,
            earnedSplitExclusion: earnedSplitExclusion,
            // Keep the pending reset candidate separate from the active workday split tracker until
            // a later sleeper-berth period is available to pair with it.
            offDutySplitPeriodFromStandardWorkdayReset: offDutySplitPeriodFromStandardWorkdayReset,
            splitEligibleConsecutiveOffStartTime: splitEligibleConsecutiveOffStartTime,
            consecutiveSleeperBerthCount: consecutiveSleeperBerthCount,
            longestSleeperBerthInConsecutiveOff: longestSleeperBerthInConsecutiveOff,
            carriedResetPairingSleeperStart: carriedResetPairingSleeperStart,
            resetInfoBeforeCarriedResetPairing: resetInfoBeforeCarriedResetPairing,
            replacedPreviousWorkdayResetInfoForCarriedPairing: replacedPreviousWorkdayResetInfoForCarriedPairing,
            offDutyMinimum: offDutyMinimum,
            reducedRestUsed: reducedRestUsed
        };
        updateWorkday(state, startDate, drivingLimit, dutyLimit, offDutyMinimum);
    },
    updateWorkday = (
        state: IState,
        startDate: Date,
        drivingLimit: number | undefined,
        dutyLimit: number | undefined,
        offDutyMinimum: number | undefined
    ) => {
        if (state.config.rest?.onDutyCountTowardRest) {
            state.workday.timeUntilMustRest = state.config.rest.betweenRestLimit;
            state.workday.consecutiveRestCount = 0;
            state.workday.consecutiveRestStartTime = undefined;
        }
        if (state.config.workday) {
            state.workday.start = startDate;
            updateWorkdayEnd(state, startDate);
            updateWorkdayDrivingLimit(state, drivingLimit);
            updateWorkdayDutyLimit(state, dutyLimit);
            updateWorkdayOffDutyMinimum(state, offDutyMinimum);
            createSplitSB(state);
            state.workdays.push(state.workday);
        }
    },
    updateWorkdayEnd = (state: IState, startDate: Date) => {
        const workdayMaximum = getConfigWorkdayMaximum(state);
        if (workdayMaximum) {
            let workdayExtended = state.workday.workdayExtended || 0;
            let workdayDeferral = 0;
            if (state.workday.reducedRestUsed) {
                workdayDeferral = state.workday.isExemptionReducedRestApplied
                    ? -state.workday.reducedRestUsed * MINUTES
                    : state.workday.reducedRestUsed * MINUTES;
            }
            state.workday.end = new Date(startDate.getTime() + workdayMaximum + workdayExtended + workdayDeferral);
        }
    },
    updateWorkdayDrivingLimit = (state: IState, drivingLimit: number | undefined) => {
        if (state?.config?.workday?.driveMaximum) {
            state.workday.drivingLimit = drivingLimit || state.config.workday.driveMaximum + (state.workday.drivingExtended || 0);
            state.workday.driveCount = 0;
        }
    },
    updateWorkdayDutyLimit = (state: IState, dutyLimit: number | undefined) => {
        if (state?.config?.workday?.dutyMaximum) {
            state.workday.dutyLimit = dutyLimit || state.config.workday.dutyMaximum;
        }
        state.workday.onDutyCount = 0;
    },
    updateWorkdayOffDutyMinimum = (state: IState, offDutyMinimum: number | undefined) => {
        if (offDutyMinimum !== undefined) {
            state.workday.offDutyMinimum = offDutyMinimum;
        } else if (state.day.deferralStatus === "DayTwo" && state.config.workday?.offDutyMinimum && state.day.deferralTime) {
            // TODO: deferralTime should not be added to workday.offDutyMinimum. See MYG-41931 (issue rejected; problem still exists but fix is blocked)
            state.workday.offDutyMinimum = state.config.workday?.offDutyMinimum + state.day.deferralTime;
        } else if (state?.config?.workday?.offDutyMinimum) {
            let workdayDeferral = 0;
            if (state.workday.reducedRestUsed) {
                workdayDeferral = state.workday.isExemptionReducedRestApplied
                    ? -state.workday.reducedRestUsed * MINUTES
                    : state.workday.reducedRestUsed * MINUTES;
            }
            state.workday.offDutyMinimum = state.config.workday.offDutyMinimum + workdayDeferral;
        }
    },
    // Keep a tally of all DutyStatusLogType counts for each day
    allocateCounts = (state: IState) => {
        let status = state.current.status;
        let isOnDuty = onDutyStatuses.indexOf(status) !== -1;

        if (!isOnDuty && offDutyStatuses.indexOf(status) === -1) {
            return;
        }

        let isCanada = !!state.config.daily;
        let isDriving = status === "D";
        if (state.currentDateIndex === state.nextDateIndex) {
            tryStartNewDay(state, state.currentDate, state.currentDateIndex);
            if (isCanada) {
                canadianDutyDeferral(state, state.currentDateIndex);
            }
            let diff = state.duration;
            allocateCountsForDay(state, status, diff, isOnDuty, isDriving);
            return;
        }

        let loopDate = new Date(state.currentDate.getTime());
        let iterationCount = 0;
        let durationLeft = state.duration;
        let isStartOfDay = false;
        while (loopDate <= state.nextDate) {
            if (iterationCount++ >= MAX_DAY_ITERATION_THRESHOLD) {
                throw new Error(
                    `Infinite loop detected: startDate: ${new Date(state.currentDate.getTime()).toISOString()}, loopDate: ${loopDate.toISOString()}`
                );
            }

            let dateIndex = getDateIndex(loopDate, state.toLocalTime);
            tryStartNewDay(state, loopDate, dateIndex, isStartOfDay);
            if (isCanada) {
                canadianDutyDeferral(state, dateIndex);
            }
            let currentTime = loopDate.getTime();
            loopDate = new Date(getBeginningOfNextDay(loopDate, state.toLocalTime, state.toUtcTime).getTime());
            let diff = Math.min(loopDate.getTime() - currentTime, durationLeft);
            if (diff > 0) {
                durationLeft = durationLeft - diff;
                allocateCountsForDay(state, status, diff, isOnDuty, isDriving);
            }
            isStartOfDay = true;
        }
    },
    allocateCountsForDay = (state: IState, status: string, diff: number, isOnDuty: boolean, isDriving: boolean) => {
        if (diff > 0) {
            if (state.day.statusCounts[status] === undefined) {
                state.day.statusCounts[status] = diff;
            } else if (status === "PC_Exempted") {
                state.day.statusCounts["PC"] += diff;
            } else {
                state.day.statusCounts[status] += diff;
            }

            if (isOnDuty) {
                // count on-duty
                state.day.onDutyCount += diff;
                state.cycle.onDutyCount += diff;
                state.cycle.dutyCountSince24HourRestPeriod += diff;
                if (isDriving) {
                    // count driving
                    state.day.driveCount += diff;
                    state.cycle.driveCount += diff;
                }
                if (state.day.consecutiveOff !== undefined && state.day.unCountedOff !== undefined) {
                    state.day.consecutiveOff = 0;
                    state.day.unCountedOff = 0;
                }
            } else {
                // count off-duty
                if (state.day.consecutiveOff !== undefined && state.day.unCountedOff !== undefined) {
                    state.day.consecutiveOff += diff;
                    state.day.unCountedOff += diff;
                }
                if (
                    state.config.rest &&
                    state.day.unCountedOff &&
                    state.day.consecutiveOff &&
                    state.day.consecutiveOff >= state.config.rest.restBreakMinimum
                ) {
                    allocateOffAndRestTime(state, state.day.unCountedOff);
                    state.day.unCountedOff = 0;
                }
            }
            state.day.hoursInCycle = getCycleCounts(state.cycle, state.config);
        }
    },
    countRestBreaks = (state: IState) => {
        // 395.3 (a)(3)(ii) Rest breaks
        if (!state.config.rest || !state.config.rest.restBreakMinimum) {
            return;
        }
        // 2020 Federal ruleset rest break revision
        if (
            state.workday.consecutiveRestCount !== undefined &&
            (!state.workday.hasStarted || state.workday.consecutiveRestCount >= state.config.rest.restBreakMinimum)
        ) {
            state.workday.timeUntilMustRest = state.config.rest.betweenRestLimit;
            state.workday.endOfLastBreak = new Date(state.nextDate.getTime());
        } else if (!state.workday.hasStarted || state.workday.consecutiveOffCount >= state.config.rest.restBreakMinimum) {
            state.workday.endOfLastBreak = new Date(state.nextDate.getTime());
        } else if (state.config.rest.earlyRestBreaks) {
            for (let i = 0; i < state.config.rest.earlyRestBreaks.length; i++) {
                let curRestBreakConfig = state.config.rest.earlyRestBreaks[i];
                let hasExceededBetweenRestLimit = state.workday.endOfLastBreak
                    ? state.current.dateTime.getTime() - state.workday.endOfLastBreak.getTime() > curRestBreakConfig.betweenRestLimit
                    : false;
                if (
                    state.workday.consecutiveRestCount !== undefined &&
                    (!state.workday.hasStarted ||
                        (!hasExceededBetweenRestLimit && state.workday.consecutiveRestCount >= curRestBreakConfig.restBreakMinimum))
                ) {
                    state.workday.timeUntilMustRest = state.config.rest.betweenRestLimit;
                    state.workday.endOfLastBreak = new Date(state.nextDate.getTime());
                    break;
                }
            }
        }
    },
    // eslint-disable-next-line complexity
    countDailyRestBreaks = (state: IState, additionalOffDutyTime: number, isLastLog = false) => {
        let restBreakMin = state.config.rest && state.config.rest.restBreakMinimum;
        let waitTime = state.workday.consecutiveWtCount;
        let enoughTimeForRestBreak = restBreakMin && (state.workday.consecutiveOffCount >= restBreakMin || waitTime >= restBreakMin);
        let doesNextLogContinueWt = !isLastLog && state.current.status === "WT" && state.next.status === "WT";

        // should count daily rest break for WT and OFF duty separately
        let isOFFDuty = (status: THosStatus) => onDutyStatuses.indexOf(status) === -1 && status !== "WT";
        let doesNextLogContinueOff = !isLastLog && isOFFDuty(state.current.status) && isOFFDuty(state.next.status);

        if (!restBreakMin || !state.day.restInfo || !enoughTimeForRestBreak || doesNextLogContinueWt || doesNextLogContinueOff) {
            return;
        }
        // take any additional time as daily rest break
        let consecutiveOffEnd = state.nextDate.getTime();
        let consecutiveOffStart = consecutiveOffEnd - state.workday.consecutiveOffCount - waitTime;
        let numDays = getNumberOfDaysBetween(new Date(consecutiveOffStart), state.nextDate, state.toLocalTime);
        let additionalBreakTime = waitTime > 0 ? waitTime : additionalOffDutyTime;
        while (numDays >= 0 && additionalBreakTime > 0) {
            let isLastDay = numDays === 0;
            let currentDay = state.days[state.days.length - 1 - numDays];
            let remainingDailyRestRequired = getRemainingDailyRestRequired(currentDay, state.config);
            if (
                currentDay !== undefined &&
                currentDay.restInfo &&
                currentDay.restCount !== undefined &&
                remainingDailyRestRequired !== undefined &&
                (isLastDay || remainingDailyRestRequired > 0)
            ) {
                let startOfDay = currentDay.date.getTime();
                let startOfNextDay = getBeginningOfNextDay(currentDay.date, state.toLocalTime, state.toUtcTime).getTime();
                let startOfAdditionalBreakTimeCurrentDay = Math.max(startOfDay, consecutiveOffStart);
                let endOfAdditionalBreakTimeCurrentDay = Math.min(startOfNextDay, consecutiveOffEnd);
                /* todo: this next line is required for the logic "deferral cannot come from core 8h rest", but it shouldn't be here and is probably super buggy...
                     Ex. it adds the deferralTime regardless of if its DayOne or DayTwo??? */
                remainingDailyRestRequired = Math.max(0, remainingDailyRestRequired + (currentDay.deferralTime ?? 0));
                let restTimeCurrentDay = Math.min(
                    additionalBreakTime,
                    endOfAdditionalBreakTimeCurrentDay - startOfAdditionalBreakTimeCurrentDay,
                    remainingDailyRestRequired
                );
                if (restTimeCurrentDay > 0) {
                    // avoid bisecting an 8h consecutive off-duty period that spans multiple days by placing the restInfo at the end of the off-duty
                    let placeRestAtEndOfConsecutiveOff: boolean =
                        isLastDay &&
                        getBeginningOfDay(currentDay.date, state.toLocalTime, state.toUtcTime).getTime() !==
                            getBeginningOfDay(new Date(consecutiveOffStart), state.toLocalTime, state.toUtcTime).getTime() &&
                        state.config.workday !== undefined &&
                        endOfAdditionalBreakTimeCurrentDay - startOfAdditionalBreakTimeCurrentDay >= state.config.workday.offDutyMinimum &&
                        endOfAdditionalBreakTimeCurrentDay - startOfAdditionalBreakTimeCurrentDay - restTimeCurrentDay <
                            state.config.workday.offDutyMinimum;
                    let [fromDate, toDate] = placeRestAtEndOfConsecutiveOff
                        ? [new Date(consecutiveOffEnd - restTimeCurrentDay), new Date(consecutiveOffEnd)]
                        : [new Date(startOfAdditionalBreakTimeCurrentDay), new Date(startOfAdditionalBreakTimeCurrentDay + restTimeCurrentDay)];
                    let isTemp = isLastLog && isLastDay;
                    let isWt = waitTime > 0;
                    currentDay.restInfo.push({ fromDate, toDate, isTemp, isWt });
                    currentDay.restCount += restTimeCurrentDay;
                    additionalBreakTime -= restTimeCurrentDay;
                    if (isTemp) {
                        if (isWt) {
                            state.workday.consecutiveWtCount -= restTimeCurrentDay;
                        } else {
                            state.workday.consecutiveOffCount -= restTimeCurrentDay;
                        }
                    }
                }
            }
            numDays -= 1;
        }
    },
    // MYG-16078: For Canadian rulesets, count all workday reset time as DailyRest if it is not the first reset for the day and the consecutiveOffDuty time does not overlap any other day.
    // Not redundant with countDailyRestBreaks: that only credits off-duty time beyond the 8h used for the reset, so a second 8h-10h reset would get little or no DailyRest.
    checkGenerousDailyRestCanadaHos = (state: IState) => {
        let remainingDailyRestRequired = getRemainingDailyRestRequired(state.day, state.config);
        if (!state.day.restInfo || !remainingDailyRestRequired || state.day.restCount === undefined || state.workday.consecutiveOffCount === 0) {
            return;
        }

        // if there are no workdays to work with, exit
        if (!state.workdays.length || !state.workday.start) {
            return;
        }

        // if the workday started before or at the start of the day, exit
        if (state.workday.start.getTime() <= state.day.date.getTime()) {
            return;
        }

        // Check if we're in an off-duty log immediately following a workday reset that occurred earlier today
        const hasPreviousWorkday = state.workdays.length >= 2;
        if (!hasPreviousWorkday) {
            return;
        }
        let isCurrentlyOffDuty = offDutyStatuses.indexOf(state.current.status) !== -1;
        let previousWorkday = state.workdays[state.workdays.length - 2];
        let isPreviousWorkdayFirstResetOfDay = previousWorkday.start && previousWorkday.start.getTime() <= state.day.date.getTime();
        let consecutiveOffStartSpan = state.nextDate.getTime() - state.workday.consecutiveOffCount;
        let previousWorkdayResetOverlapsCurrentOffDuty = previousWorkday.endOfLastBreak && previousWorkday.endOfLastBreak.getTime() >= consecutiveOffStartSpan;
        if (
            isCurrentlyOffDuty &&
            isPreviousWorkdayFirstResetOfDay &&
            previousWorkdayResetOverlapsCurrentOffDuty
        ) {
            return;
        }

        // if the current workday reset overlaps start or end of day, do nothing and return
        let consecutiveOffStart = new Date(state.nextDate.getTime() - state.workday.consecutiveOffCount);
        if (getDateIndex(state.nextDate, state.toLocalTime) !== getDateIndex(consecutiveOffStart, state.toLocalTime)) {
            return;
        }
        // use only the rest period's overlap on the day
        let restInfo = getPeriodOverlap(
            state.day.date,
            new Date(getBeginningOfNextDay(state.day.date, state.toLocalTime, state.toUtcTime).getTime() - 1000),
            consecutiveOffStart,
            new Date(consecutiveOffStart.getTime() + remainingDailyRestRequired)
        );
        if (!restInfo) {
            return;
        }

        // Merge overlapping rest periods to avoid duplicates:
        // If a driver was off from 8:00 AM to 4:30 PM (8.5h total) and 8h was required for a reset,
        // countDailyRestBreaks already logged the 30min "extra" (8:00 AM – 8:30 AM).
        // Since we are now adding a 2h rest period from 8:00 AM – 10:00 AM, we must remove
        // the existing 30min entry to avoid double-counting the time starting at 8:00 AM.
        for (let i = 0; i < state.day.restInfo.length; i++) {
            const existing = state.day.restInfo[i];
            // Check if rest periods overlap (start or end falls within the other period)
            if (restInfo.fromDate.getTime() === existing.fromDate.getTime())
            {
                state.day.restCount -= existing.toDate.getTime() - existing.fromDate.getTime();
                state.day.restInfo.splice(i, 1);
                break;
            }
        }

        // update remainingDailyRestRequired and restInfo after removing any overlapping restInfo
        remainingDailyRestRequired = getRemainingDailyRestRequired(state.day, state.config);
        if (!remainingDailyRestRequired) {
            return;
        }

        restInfo = getPeriodOverlap(
            state.day.date,
            new Date(getBeginningOfNextDay(state.day.date, state.toLocalTime, state.toUtcTime).getTime() - 1000),
            consecutiveOffStart,
            new Date(consecutiveOffStart.getTime() + remainingDailyRestRequired)
        );
        if (!restInfo) {
            return;
        }

        // add a restInfo from the current reset's offDuty time
        state.day.restCount += remainingDailyRestRequired;
        state.day.restInfo.push(restInfo);
    },
    /* MYG-16078: For Canadian rulesets, if a DailyRest violation was caused by an unneeded workday reset, remove the violation.
    A workday reset is "unneeded" when no workday violations would exist if the reset did not happen. */
    checkIfUnneededWorkdayResetCausedDailyRestViolationCanadaHos = (state: IState) => {
        if (
            !state.config.workday ||
            state.config.workday.workdayMaximum === undefined ||
            state.config.workday.driveMaximum === undefined ||
            state.config.workday.dutyMaximum === undefined ||
            !state.config.rest ||
            state.config.rest.dailyRestMinimum === undefined
        ) {
            return;
        }
        // Given two adjacent workdays, return true if merging them would cause any workday violation
        let doesMergeCreateViolation = (workday1: IWorkday, workday2: IWorkday): boolean => {
            // DutyViolation - If combined onDutyCount exceeds workday on duty limit
            let onDutyCount = workday1.onDutyCount && workday2.onDutyCount && workday1.onDutyCount + workday2.onDutyCount;
            if (
                !state.config.workday ||
                !state.config.workday.dutyMaximum ||
                onDutyCount === undefined ||
                onDutyCount > state.config.workday.dutyMaximum
            ) {
                return true;
            }
            // DrivingViolation - If combined driveCount exceeds the workday drive limit
            let driveCount = workday1.driveCount && workday2.driveCount && workday1.driveCount + workday2.driveCount;
            if (
                !state.config.workday ||
                !state.config.workday.driveMaximum ||
                driveCount === undefined ||
                driveCount > state.config.workday.driveMaximum
            ) {
                return true;
            }
            // WorkdayViolation - If time from workday1's start to workday2's startOfLastBreak exceeds the workday length limit
            // Does not use workday1.end to avoid utilizing Canada's split sleeper workday extension to remove DailyRest violations
            // Do not need to check workday1's endOfLastDLog because there would already be a Workday violation
            let endOfWorkday1 =
                workday1.start && state.config.workday.workdayMaximum && workday1.start.getTime() + state.config.workday.workdayMaximum;
            let didDrivePastEndOfWorkday = endOfWorkday1 && workday2.endOfLastDLog && workday2.endOfLastDLog.getTime() > endOfWorkday1;
            if (!endOfWorkday1 || didDrivePastEndOfWorkday) {
                return true;
            }
            return false;
        };
        // Returns a new array of restInfos that will satisfy the restCount. If no variation of restInfos can be found, an empty array is returned instead.
        let getNewRestInfos = (day: IDay, consecutiveOffStart: Date, consecutiveOffEnd: Date): IRestInfo[] => {
            let totalDailyRestRequired = getTotalDailyRestRequired(day, state.config);
            let remainingDailyRestRequired = getRemainingDailyRestRequired(day, state.config);
            if (day.restCount === undefined || totalDailyRestRequired === undefined || remainingDailyRestRequired === undefined || !day.restInfo) {
                return [];
            }
            let dayStart = day.date.getTime();
            let dayEnd = getBeginningOfNextDay(day.date, state.toLocalTime, state.toUtcTime).getTime();
            let newRestInfos: IRestInfo[] = [];
            for (let i = 0; i < day.restInfo.length; i++) {
                let restInfo = day.restInfo[i];
                if (doPeriodsOverlap(consecutiveOffStart, consecutiveOffEnd, restInfo.fromDate, restInfo.toDate)) {
                    remainingDailyRestRequired += restInfo.toDate.getTime() - restInfo.fromDate.getTime();
                } else {
                    newRestInfos.push(restInfo);
                }
            }
            let newRestStart = Math.max(dayStart, consecutiveOffStart.getTime());
            let newRestEnd = Math.min(dayEnd, newRestStart + remainingDailyRestRequired);
            // If the new rest period does not cover the required rest
            if (newRestEnd - newRestStart < remainingDailyRestRequired) {
                return [];
            }
            return [...newRestInfos, { fromDate: new Date(newRestStart), toDate: new Date(newRestEnd) }];
        };
        let tryFixViolation = (violationIndex: number, dateIndex: string) => {
            let dayOfViolation;
            if (!(dayOfViolation = state.days.find(day => day.dateIndex === dateIndex))) {
                return;
            }
            // get workdays that overlap the day of violation
            let overlappingWorkdays: IWorkday[] = [];
            overlappingWorkdays = state.workdays.filter(
                workday =>
                    workday.start &&
                    workday.end &&
                    doPeriodsOverlap(
                        workday.start,
                        workday.end,
                        dayOfViolation.date,
                        new Date(getBeginningOfNextDay(dayOfViolation.date, state.toLocalTime, state.toUtcTime).getTime() - 1000)
                    )
            );
            // If the first workday starts after beginning of the dayOfViolation, there is an "assumed" workday that came before it, that also overlaps the day of violation.
            let numberOfOverlappingWorkdays =
                overlappingWorkdays.length + (state.workdays[0].start && state.workdays[0].start.getTime() > dayOfViolation.date.getTime() ? 1 : 0);
            // If 2 or 3 workdays overlap the day of violation, get the following workday; it is needed for the calculation as it can be used as the second workday in the merge.
            if (numberOfOverlappingWorkdays === 2 || numberOfOverlappingWorkdays === 3) {
                let lastWorkday = overlappingWorkdays[overlappingWorkdays.length - 1];
                if (!lastWorkday) {
                    return;
                }
                let workdayIndex = state.workdays.indexOf(lastWorkday, overlappingWorkdays.length - 1);
                let followingWorkday = state.workdays[workdayIndex + 1];
                if (!followingWorkday) {
                    return;
                }
                overlappingWorkdays.push(followingWorkday);
            }
            // If any 2 adjacent workdays can be merged, remove the day's DailyRest violation and update restInfo and restCount
            for (let workdayIndex = 0; workdayIndex < overlappingWorkdays.length - 1; workdayIndex++) {
                let workday1 = overlappingWorkdays[workdayIndex];
                let workday2 = overlappingWorkdays[workdayIndex + 1];
                if (!workday1 || !workday2 || !workday1.endOfLastBreak || doesMergeCreateViolation(workday1, workday2)) {
                    continue;
                }
                let consecutiveOffStart = new Date(workday1.endOfLastBreak.getTime() - workday1.consecutiveOffCount);
                let newRestInfos = getNewRestInfos(dayOfViolation, consecutiveOffStart, workday1.endOfLastBreak);
                // If enough rest was found to satisfy the daily requirement, remove the day's DailyRest violation and update restInfo and restCount
                let dayOfViolationTotalRestRequired = getTotalDailyRestRequired(dayOfViolation, state.config);
                if (newRestInfos.length && dayOfViolationTotalRestRequired && dayOfViolation.restCount !== undefined) {
                    state.pastViolations.splice(violationIndex, 1);
                    dayOfViolation.restInfo = [...newRestInfos];
                    dayOfViolation.restCount = dayOfViolationTotalRestRequired;
                    break;
                }
            }
        };
        // Filter out any dailyRest violations that have another daily violation on the same day.
        let dailyRestViolations: { violationIndex: number; dateIndex: string }[] = [];
        let otherDailyViolationDates: Record<string, 1> = {};
        state.pastViolations.forEach((violation, violationIndex) => {
            let dateIndex = getDateIndex(violation.fromDate, state.toLocalTime);
            if (violation.type === "DailyRest") {
                dailyRestViolations.push({ violationIndex, dateIndex });
            } else if (["DailyDriving", "DailyDuty", "DailyOff"].some(otherDailyType => otherDailyType === violation.type)) {
                otherDailyViolationDates[dateIndex] = 1;
            }
        });
        dailyRestViolations = dailyRestViolations.filter(violationObj => !otherDailyViolationDates[violationObj.dateIndex]);
        // Sort by violationIndex, decreasing order, so that iteration moves from the end of the array to the beginning.
        // violationIndex is the index of the violation in the state.pastViolations array
        // This allows for elements to be removed from the array during iteration without disrupting the next iteration.
        dailyRestViolations.sort((a, b) => b.violationIndex - a.violationIndex);
        // Try to remove each DailyRest violation
        dailyRestViolations.forEach(violationObj => tryFixViolation(violationObj.violationIndex, violationObj.dateIndex));
    },
    checkIfWorkdayReset = (state: IState) => {
        // Workday off-duty reset
        if (state.workday.offDutyMinimum) {
            let restBreakMin = state.config.rest && state.config.rest.restBreakMinimum;
            if (!state.workday.hasStarted || isWorkdayReadyForReset(state)) {
                // workday reset completed.
                checkSplitPeriodsDuringWorkdayReset(state);
                checkGenerousDailyRestCanadaHos(state);
                const resetInfo = state.workdays.length > 0
                    ? calculateOffDutyMinimumResetInfo(state.workday, state.nextDate)
                    : undefined;
                startNewWorkday(state, state.nextDate, false, undefined, resetInfo);
            } else if (restBreakMin && state.workday.consecutiveOffCount >= restBreakMin) {
                // count short consecutive-off towards daily break
                let additionalOffDutyTime = getAdditionalConsecutiveOffDutyTime(state);
                countDailyRestBreaks(state, additionalOffDutyTime);
            }
        }
    },
    // returns the current consecutiveOffDuty time that has not been used to reset the workday
    getAdditionalConsecutiveOffDutyTime = (state: IState): number => {
        if (offDutyStatuses.indexOf(state.current.status) === -1) {
            return 0;
        }
        let unusedOffDutyTime = state.workday.consecutiveOffCount;
        let prevWorkday = state.workdays[state.workdays.length - 2];
        // if any of the current consecutiveOff was used to reset a workday, subtract that time
        if (prevWorkday) {
            if (prevWorkday.completedSplitSB && prevWorkday.endOfLastBreak && prevWorkday.lastSBPeriodMinimum !== undefined) {
                // previous workday was completed with SB, subtract time required for the split
                if (prevWorkday.splitSB && prevWorkday.splitSB.periods?.length >= 2) {
                    // passenger split
                    let lastSBPeriod = prevWorkday.splitSB.periods[1];
                    let consecutiveOffStart = new Date(state.nextDate.getTime() - state.workday.consecutiveOffCount);
                    // if the current consecutiveOff contains the previous workday's last SB period, subtract the length of the SB period
                    if (doPeriodsOverlap(new Date(consecutiveOffStart), state.nextDate, lastSBPeriod.fromDate, lastSBPeriod.toDate)) {
                        unusedOffDutyTime -= prevWorkday.lastSBPeriodMinimum;
                    }
                }
            } else if (!state.workday.hasStarted && prevWorkday.offDutyMinimum !== undefined) {
                // previous workday was completed by offDutyMinimum, subtract the offDutyMinimum
                unusedOffDutyTime -= prevWorkday.offDutyMinimum;
            }
        }
        return Math.max(0, unusedOffDutyTime);
    },
    canStatusExtendPeriodType = (status: string, periodType: TSplitPeriodType) => {
        const isOffDutyStatus = offDutyStatuses.indexOf(status) !== -1;
        const isSBStatus = status === "SB";
        return periodType === "offDuty" ? (isOffDutyStatus || isSBStatus) : isSBStatus;
    },
    getFinalPeriodToExtend = (resetInfo: TWorkdayResetInfo) => {
        if (resetInfo.type === "offDutyMinimum") {
            return resetInfo;
        }
        if (resetInfo.type === "asymmetricSplitSleeperBerth" || resetInfo.type === "symmetricSplitSleeperBerth") {
            const periods = resetInfo.periods;
            return periods.length > 1 ? periods[1] : periods[0];
        }
        return undefined;
    },
    tryExtendPreviousWorkdayResetInfo = (state: IState, toDate: Date) => {
        const previousWorkday = state.workdays.length > 1
            ? state.workdays[state.workdays.length - 2]
            : undefined;
        if (!previousWorkday?.resetInfo) {
            return;
        }
        // ERR path: reconcile actual rest duration instead of extending toDate
        if (previousWorkday.isExemptionReducedRestApplied) {
            reconcileErrRestState(state, toDate);
            return;
        }
        const periodToExtend = getFinalPeriodToExtend(previousWorkday.resetInfo);
        if (!periodToExtend) {
            return;
        }
        // don't extend past periods
        if (periodToExtend.toDate.getTime() !== state.current.dateTime.getTime()) {
            return;
        }
        // current log's status must be compatible with period type
        const periodType: TSplitPeriodType = previousWorkday.resetInfo.type === "offDutyMinimum"
            ? "offDuty"
            : (periodToExtend as IOffDutySplitPeriod | ISbSplitPeriod).type;
        if (!canStatusExtendPeriodType(state.current.status, periodType)) {
            return;
        }
        periodToExtend.toDate = new Date(toDate);
    },
    // When ERR resets the workday at the minimum eligible rest point (e.g., 4h), the driver may
    // continue resting beyond that point before going ON duty. Recalculate based on the actual
    // consecutive off time so the next workday reflects the true deferred amount.
    // Called from tryExtendPreviousWorkdayResetInfo (mid-rest, toDate = state.nextDate, or
    // driver goes ON, toDate = state.currentDate).
    reconcileErrRestState = (state: IState, resetInfoToDate: Date) => {
        if (
            state.customCodes.disableErrRestReconciliationFix ||
            state.workday.hasStarted ||
            state.workday.reducedRestUsed === undefined ||
            state.workday.isExemptionReducedRestApplied ||
            !state.config.workday?.offDutyMinimum ||
            !state.config.workday.exemptionReducedRest?.offDutyMinimum
        ) {
            return;
        }

        const actualRest = state.workday.consecutiveOffCount;
        const configMin = state.config.workday.offDutyMinimum;
        const errMin = state.config.workday.exemptionReducedRest.offDutyMinimum;
        const errWorkday = state.workdays[state.workdays.length - 2];

        if (actualRest >= configMin) {
            // Driver rested enough for a normal reset — ERR was effectively never needed.
            // Revert the ERR flags so the workday is treated as a regular reset and ERR
            // availability is restored for the next workday.
            state.workday.reducedRestUsed = undefined;
            if (errWorkday?.isExemptionReducedRestApplied) {
                errWorkday.isExemptionReducedRestApplied = false;
                errWorkday.reducedRestUsed = undefined;
                // Restore the ERR availability date that was saved before ERR fired.
                state.lastReducedRestApplyDate = errWorkday.prevLastReducedRestApplyDate;
            }
        } else if (actualRest >= errMin) {
            // ERR did apply but the driver rested longer than the 4h minimum.
            // Correct the deferred amount based on actual rest (e.g., 8h - 4.5h = 3.5h, not 4h).
            const correctedReducedRestUsed = (configMin - actualRest) / MINUTES;
            state.workday.reducedRestUsed = correctedReducedRestUsed;
            if (errWorkday?.isExemptionReducedRestApplied) {
                errWorkday.reducedRestUsed = correctedReducedRestUsed;
            }
        }

        if (errWorkday?.resetInfo?.type === "offDutyMinimum") {
            errWorkday.resetInfo.toDate = new Date(resetInfoToDate.getTime());
        }
    },
    // Persist this separately from state.logs: incremental validation retains only new logs, but the reset's
    // split-leg eligibility depends on every status in the current rest period. Zero-length logs are skipped like
    // evaluateAsymmetricSplit skips them, so they neither split a sleeper-berth run nor veto the rest.
    trackSplitLegEligibilityOfConsecutiveOff = (state: IState, isStartOfConsecutiveOff: boolean) => {
        if (state.duration === 0) {
            return;
        }
        if (isStartOfConsecutiveOff) {
            state.workday.splitEligibleConsecutiveOffStartTime = state.current.status === "WT" ? undefined : state.current.dateTime;
            state.workday.consecutiveSleeperBerthCount = state.current.status === "SB" ? state.duration : 0;
            state.workday.longestSleeperBerthInConsecutiveOff = state.workday.consecutiveSleeperBerthCount;
            return;
        }
        if (state.current.status === "WT") {
            state.workday.splitEligibleConsecutiveOffStartTime = undefined;
        } else if (state.workday.splitEligibleConsecutiveOffStartTime === undefined) {
            state.workday.splitEligibleConsecutiveOffStartTime = state.current.dateTime;
        }
        state.workday.consecutiveSleeperBerthCount =
            state.current.status === "SB" ? (state.workday.consecutiveSleeperBerthCount ?? 0) + state.duration : 0;
        state.workday.longestSleeperBerthInConsecutiveOff = Math.max(
            state.workday.longestSleeperBerthInConsecutiveOff ?? 0,
            state.workday.consecutiveSleeperBerthCount
        );
    },
    // eslint-disable-next-line complexity
    countOffDutyTime = (state: IState) => {
        if (!state.cycle.pauseCountingCycleOff) {
            state.cycle.cycleConsecutiveOffCount += state.duration;
        }

        if (state.customCodes.disableErrRestReconciliationFix) {
            // Original call order: before consecutiveOffCount is updated, matching the original implementation.
            updateEligibleReducedRestPeriodsOld(state, true);
        }

        state.workday.consecutiveOffCount =
            state.current.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt !== false
                ? state.duration + state.workday.consecutiveOffCount
                : 0;
        state.workday.consecutiveWtCount = state.current.status === "WT" ? state.workday.consecutiveWtCount + state.duration : 0;

        if (
            state.workday.consecutiveOffCount === state.duration &&
            (state.current.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt !== false)
        ) {
            // TODO: This condition fires incorrectly after countDailyRestBreaks (called below at
            // the `countDailyRestBreaks` call) zeroes consecutiveOffCount during temp-log processing
            // (isTemp=true path). On the next log, consecutiveOffCount === duration again, causing
            // consecutiveOffStartTime to be overwritten with the wrong (later) break start time.
            // This makes consecutiveOffStartTime an unstable anchor for the nextAvailabilityEvent
            // calculator, which had to work around it by checking reducedRestLogs.length > 0 to detect
            // pending ExemptionReducedRest exemptions (workdayDuty.ts).
            //
            // Root cause: countDailyRestBreaks subtracts restTimeCurrentDay from consecutiveOffCount
            // when isTemp=true, which can bring the count to 0. The consecutiveOffCount===duration
            // condition cannot distinguish between "genuine start (driver was on-duty before)" and
            // "count was zeroed by countDailyRestBreaks". The WT case (lines above) intentionally
            // zeroes the count to signal a new break start, adding further complexity.
            //
            // Potential fix: Track a separate workday.consecutiveOffBreakStartTime that is set ONLY
            // on genuine status transitions (on-duty → off-duty) and is never affected by
            // countDailyRestBreaks. This would allow nextAvailabilityEvent calculators to predict
            // workday resets reliably without the reducedRestLogs coverage-gap workaround.
            state.workday.consecutiveOffStartTime = state.current.dateTime;
            trackSplitLegEligibilityOfConsecutiveOff(state, true);
        } else if (state.workday.consecutiveOffCount > 0) {
            trackSplitLegEligibilityOfConsecutiveOff(state, false);
        }

        if (!state.customCodes.disableErrRestReconciliationFix) {
            // New call order: after consecutiveOffCount is updated, so the precise period.end can be calculated.
            updateEligibleReducedRestPeriods(state, true);
        }

        if (state.workday.consecutiveRestCount !== undefined) {
            state.workday.consecutiveRestCount += state.duration;
            // For onDutyCountTowardRest rulesets, record the start of the rest period so that
            // workday.ts can compute a stable breakQualifyDate without sliding with evalTime.
            // Guarded by onDutyCountTowardRest: for standard rulesets, consecutiveOffStartTime
            // (set just above) is the anchor used by workday.ts instead.
            if (state.config.rest?.onDutyCountTowardRest && state.workday.consecutiveRestCount === state.duration) {
                state.workday.consecutiveRestStartTime = state.current.dateTime;
            }
        }
        countRestBreaks(state);

        // Extend previous workday reset info if applicable (handles ERR reconciliation for ERR workdays)
        tryExtendPreviousWorkdayResetInfo(state, state.nextDate);

        if (state.workday.offDutyMinimum !== undefined) {
            let isLastLog = state.i >= state.logs.length;
            // subtract offDutyMinimum to prevent daily rest breaks during a reset period
            let additionalOffDutyTime =
                state.workday.hasStarted && !isLastLog
                    ? getAdditionalConsecutiveOffDutyTime(state) - state.workday.offDutyMinimum
                    : getAdditionalConsecutiveOffDutyTime(state);
            countDailyRestBreaks(state, additionalOffDutyTime, isLastLog);
        }

        let cycleConfig = state.config.cycle;
        if (!cycleConfig) {
            return;
        }
        if (cycleConfig.cycleRestartMinimum) {
            if (state.cycle.cycleConsecutiveOffCount >= cycleConfig.cycleRestartMinimum) {
                if (state.config.resetPreviousCycle && state.cycle.hasStarted) {
                    let extraOffDuty = state.cycle.cycleConsecutiveOffCount - cycleConfig.cycleRestartMinimum;
                    let completedRestartDate = new Date(state.nextDate.getTime() - extraOffDuty);
                    if (
                        state.rulesetDateRanges.some(
                            range =>
                                range.start !== null && range.end !== null && range.start <= completedRestartDate && range.end >= completedRestartDate
                        )
                    ) {
                        state.cycle.completedRestartDate = completedRestartDate;
                    }
                }
                recordCycleResetPeriod(state, getDateRestBecameLongEnough(state, cycleConfig.cycleRestartMinimum));
                startNewCycle(state, state.nextDate);
            } else if (state.cycleRestartDatesFromOtherRulesets?.length) {
                let nextCycleRestartDateFromOtherRuleset =
                    state.cycleRestartDatesFromOtherRulesets[state.cycleRestartDatesFromOtherRulesets.length - 1];
                if (nextCycleRestartDateFromOtherRuleset.getTime() <= state.nextDate.getTime()) {
                    recordCycleResetPeriod(state, nextCycleRestartDateFromOtherRuleset);
                    startNewCycle(state, state.nextDate);
                }
            }
        }

        // Canada section 25
        if (cycleConfig.cycleRestMinimum && state.cycle.cycleConsecutiveOffCount >= cycleConfig.cycleRestMinimum) {
            fillRestPeriodsEndDates(state);
            state.cycle.dutyCountSince24HourRestPeriod = 0;
        }
    },
    fillRestPeriodsEndDates = (state: IState) => {
        const cycleConfig = state.config.cycle;
        if (!cycleConfig) {
            return;
        }
        const restPeriods = cycleConfig.cycleRestPeriods ?? 1;
        if (restPeriods === 1) {
            state.cycle.completedRestPeriodDates[0] = new Date(state.nextDate.getTime());
        } else if (cycleConfig.cycleRestMinimum) {
            const usedPeriods = Math.floor(state.cycle.cycleConsecutiveOffCount / cycleConfig.cycleRestMinimum);
            let lastRestPeriod =
                state.cycle.completedRestPeriodDates.length > 0
                    ? state.cycle.completedRestPeriodDates[state.cycle.completedRestPeriodDates.length - 1].getTime()
                    : 0;
            let isNewRest = state.nextDate.getTime() - lastRestPeriod >= cycleConfig.cycleRestMinimum || usedPeriods >= restPeriods;
            for (let i = usedPeriods - 1; i >= 0; i--) {
                let currentPeriod = new Date(state.nextDate.getTime() - i * cycleConfig.cycleRestMinimum);
                if (isNewRest) {
                    if (state.cycle.completedRestPeriodDates.length === restPeriods) {
                        state.cycle.completedRestPeriodDates.shift();
                    }
                    state.cycle.completedRestPeriodDates.push(currentPeriod);
                } else {
                    let indexPart = usedPeriods > 1 && state.cycle.cycleConsecutiveOffCount % cycleConfig.cycleRestMinimum === 0 ? 0 : 1;
                    state.cycle.completedRestPeriodDates[state.cycle.completedRestPeriodDates.length - indexPart - i] = currentPeriod;
                }
            }
        }
    },
    extendWorkdayWithWt = (state: IState) => {
        // WT doesn't count towards the workday time. WT should not extend workday
        // if the workday has not yet started.
        if (state.config.workday?.wtExtendsWorkday !== false && state.workday.hasStarted && state.workday.end && state.current.status === "WT") {
            state.workday.end.setTime(state.workday.end.getTime() + state.duration);
        }
    },
    updateEligibleReducedRestPeriods = (state: IState, isOffDuty = false) => {
        if (!state.config.workday || !state.config.workday.exemptionReducedRest) {
            return;
        }
        if (isOffDuty) {
            // Start counting duration for eligible reduced rest duration when first off-duty status encountered after on-duty status.
            // After moving updateEligibleReducedRestPeriods() to be called AFTER consecutiveOffCount is updated,
            // the first off-duty log will have consecutiveOffCount === state.duration (previously was 0).
            if (state.workday.consecutiveOffCount === state.duration && (state.current.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt)) {
                if (!state.workday.eligibleReducedRestPeriods) {
                    state.workday.eligibleReducedRestPeriods = [];
                }
                state.workday.eligibleReducedRestPeriods.push({ start: state.current.dateTime });
            }

            // Close the period when the reduced rest minimum is first met, but only if the total
            // off-duty for this log is less than the full rest requirement (offDutyMax). If the
            // driver rests >= offDutyMax, it is a normal full rest — Path B will pop the period.
            if (state.workday.eligibleReducedRestPeriods && state.workday.eligibleReducedRestPeriods.length > 0) {
                let currentPeriod = state.workday.eligibleReducedRestPeriods[state.workday.eligibleReducedRestPeriods.length - 1];
                const offDutyMin = state.config.workday.exemptionReducedRest.offDutyMinimum;
                const offDutyMax = state.config.workday.offDutyMinimum;
                if (currentPeriod.start && !currentPeriod.end && state.workday.consecutiveOffCount >= offDutyMin && state.workday.consecutiveOffCount < offDutyMax) {
                    // Set period.end to when the minimum was FIRST met (start + minimum), not when the driver goes back on duty.
                    // This allows the workday to reset as soon as the reduced rest minimum is met.
                    currentPeriod.end = new Date(currentPeriod.start.getTime() + state.workday.consecutiveOffCount);
                }
            }
        } else {
            // Non-off-duty status breaks the consecutive off streak — discard the open period.
            if (!state.workday.eligibleReducedRestPeriods || state.workday.eligibleReducedRestPeriods.length === 0) {
                return;
            }
            let currentPeriod = state.workday.eligibleReducedRestPeriods[state.workday.eligibleReducedRestPeriods.length - 1];
            if (currentPeriod.start && !currentPeriod.end) {
                state.workday.eligibleReducedRestPeriods.pop();
            }
        }

    },
    updateEligibleReducedRestPeriodsOld = (state: IState, isOffDuty = false) => {
        if (!state.config.workday || !state.config.workday.exemptionReducedRest) {
            return;
        }
        if (isOffDuty) {
            // start counting duration for eligible reduced rest duration when first offDuty status encounterd after onDuty status
            if (state.workday.consecutiveOffCount === 0 && (state.current.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt)) {
                if (!state.workday.eligibleReducedRestPeriods) {
                    state.workday.eligibleReducedRestPeriods = [];
                }
                state.workday.eligibleReducedRestPeriods.push({ start: state.current.dateTime });
            }
        } else {
            // if the current status is not offDuty, end the last eligible reduced rest period
            if (!state.workday.eligibleReducedRestPeriods || state.workday.eligibleReducedRestPeriods.length === 0) {
                return;
            }
            let currentPeriod = state.workday.eligibleReducedRestPeriods[state.workday.eligibleReducedRestPeriods.length - 1];
            const offDutyMin = state.config.workday.exemptionReducedRest.offDutyMinimum;
            const offDutyMax = state.config.workday.offDutyMinimum;
            if (currentPeriod.start && !currentPeriod.end) {
                if (state.workday.consecutiveOffCount >= offDutyMin && state.workday.consecutiveOffCount < offDutyMax) {
                    currentPeriod.end = state.current.dateTime;
                } else {
                    // remove the any period that is not eligible (> 4h and < 8h)
                    state.workday.eligibleReducedRestPeriods.pop();
                }
            }
        }

        // CRITICAL: Alberta reduced rest exemption handling does not follow the standard approach of iteratively calculating each log.
        // It unfortunately waits until an off-duty period completes to apply the reduced rest exemption,
        // and as a result is required to be called before off-duty logs are counted against the state. A refactor is required, but until that happens,
        // we need this workaround of calculating the restInfo BEFORE offDutyMinimum is adjusted.
        // The complimentary piece of this hack-solution for calculating the workday reset info is in applyReducedRestAndCalculateWorkdayResetInfo.
        const isCurrentlyOffDuty = offDutyStatuses.indexOf(state.current.status) !== -1;
        if (isCurrentlyOffDuty && state.workday.eligibleReducedRestPeriods && state.workday.eligibleReducedRestPeriods.length > 0) {
            let currentPeriod = state.workday.eligibleReducedRestPeriods[state.workday.eligibleReducedRestPeriods.length - 1];
            const offDutyMin = state.config.workday.exemptionReducedRest.offDutyMinimum;
            if (currentPeriod.start && !currentPeriod.end) {
                // Calculate what consecutiveOffCount WILL BE after countOffDutyTime() adds state.duration. This is the terrible yet needed workaround mentioned above.
                const futureConsecutiveOffCount = (state.current.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt !== false)
                    ? state.duration + state.workday.consecutiveOffCount
                    : 0;
                // Only close when we've reached the minimum duration (4h). // Periods that end before reaching 4h are removed by Path B logic
                if (futureConsecutiveOffCount >= offDutyMin) {
                    currentPeriod.end = state.nextDate;
                }
            }
        }
    },
    countOnDutyTime = (state: IState) => {
        // Workday detection
        // Start of workday defined as any D or ON status occurring either after
        // workdayEnd or after 10 hrs consecutively off duty. Violation recorded
        // if after workdayEnd and no 10 hrs consecutively off preceding.
        if (state.customCodes.disableErrRestReconciliationFix) {
            updateEligibleReducedRestPeriodsOld(state);
        } else {
            updateEligibleReducedRestPeriods(state);
        }

        tryExtendPreviousWorkdayResetInfo(state, state.currentDate);
        closeCycleResetPeriodOnReturnToDuty(state);

        state.cycle.cycleConsecutiveOffCount = 0;
        state.workday.consecutiveOffCount = 0;
        state.workday.consecutiveWtCount = 0;

        state.workday.consecutiveOffStartTime = undefined;
        state.workday.splitEligibleConsecutiveOffStartTime = undefined;
        state.workday.consecutiveSleeperBerthCount = undefined;
        state.workday.longestSleeperBerthInConsecutiveOff = undefined;

        if (state.cycleRestartDatesFromOtherRulesets?.length) {
            for (let i = state.cycleRestartDatesFromOtherRulesets?.length - 1; i >= 0; i--) {
                let cycleRestartDateFromOtherRuleset = state.cycleRestartDatesFromOtherRulesets[i];
                if (cycleRestartDateFromOtherRuleset.getTime() <= state.currentDate.getTime()) {
                    state.cycleRestartDatesFromOtherRulesets.splice(i, 1);
                }
            }
        }
        if (!state.cycle.hasStarted) {
            state.cycle.fromDate = new Date(state.currentDate.getTime());
            state.cycle.toDate = new Date(state.currentDate.getTime() + (state.config.cycle?.cycleDays || 0) * DAYS);
            if (state.config.cycle && state.config.cycle.cycleRestMinimum) {
                state.cycle.completedRestPeriodDates = [new Date(state.currentDate.getTime())];
            }
            state.cycle.hasStarted = true;
        }
        if (!state.workday.hasStarted) {
            startNewWorkday(state, state.currentDate, true, undefined);
        } else {
            state.workday.hasStarted = true;
            state.workday.consecutiveOffCount = 0;

            state.workday.consecutiveOffStartTime = undefined;
        }
        if (state.workday.consecutiveRestCount !== undefined) {
            if (state.current.status !== "D") {
                state.workday.consecutiveRestCount += state.duration;
                // Set start time on the first accumulation (count just transitioned from 0).
                // Unlike consecutiveOffStartTime, countRestBreaks does not subtract from
                // consecutiveRestCount, so this condition fires exactly once per rest period.
                if (state.workday.consecutiveRestCount === state.duration) {
                    state.workday.consecutiveRestStartTime = state.current.dateTime;
                }
            } else {
                state.workday.consecutiveRestCount = 0;
                state.workday.consecutiveRestStartTime = undefined;
                if (state.workday.timeUntilMustRest !== undefined) {
                    state.workday.timeUntilMustRest -= state.duration;
                }
            }
            countRestBreaks(state);
        }
        if (state.workday.onDutyCount !== undefined) {
            state.workday.onDutyCount += state.duration;
        }
        if (state.current.status === "D") {
            // Increment workday driving count
            if (state.workday.driveCount !== undefined) {
                state.workday.driveCount += state.duration;
            }
            state.workday.endOfLastDLog = state.nextDate;
        }
    },
    /**
     * Check if the driver has exceeded their workday limit
     */
    isWorkdayExceeded = (state: IState): boolean => {
        if (!state.workday.end) {
            return false;
        }
        return state.nextDate.getTime() - state.workday.end.getTime() > 0;
    },
    /**
     * If an exemption is applied, remove or trim previous violations
     */
    removeOrTrimViolationPeriods = (state: IState, cutOffTime: Date | undefined, type: string) => {
        let violation = state.currentViolations[type];

        if (!cutOffTime || !violation) {
            return;
        }

        if (!violation.drivingPeriods) {
            delete state.currentViolations[type];
            return;
        }

        let nonExemptDrivingPeriods: IPeriod[] = [];
        for (const period of violation.drivingPeriods) {
            if (period.toDate && period.toDate.getTime() <= cutOffTime.getTime()) {
                continue;
            }

            let fromDate = new Date(Math.max(period.fromDate.getTime(), cutOffTime.getTime()));
            nonExemptDrivingPeriods.push({ fromDate: fromDate, toDate: period.toDate });
        }
        violation.drivingPeriods = nonExemptDrivingPeriods;

        if (nonExemptDrivingPeriods.length === 0) {
            delete state.currentViolations[type];
            return;
        }
        if (state.customCodes.disableRemoveOrTrimViolationsAfter16hException) {
            updateDrivingDurationAndLastDriveDateRangeOld(violation, nonExemptDrivingPeriods[0].fromDate, state.next.Date);
        } else {
            updateFromDateAndDrivingDuration(violation);
        }
    },
    /**
     * Attend to extend workday
     */
    applyBigDayOld = (state: IState) => {
        if (!state.workday.end || !state.workday.hasStarted || state.workday.workdayExtended || !state.config.bigDayBonus) {
            return;
        }

        if (state.exemption16HLogs.length > 0 && state.exemption16HLogs[0].dateTime.getTime() > state.nextDate) {
            return;
        }

        // Try and get valid 16 hour exemption for current workday.
        while (
            state.exemption16HLogs.length > 0 &&
            state.exemption16HLogs[0].dateTime.getTime() <= state.workday.end.getTime() + state.config.bigDayBonus
        ) {
            let exemption = state.exemption16HLogs.shift();
            let exemptionDate = exemption.dateTime;
            if (isExemptionAppliedDuringWorkday(state, exemptionDate) && isBigDayExemptionReset(state)) {
                // Apply big day offset, add bigDayBonus to the workday
                let bigDayOffset = state.workday.end.getTime() + state.config.bigDayBonus;
                state.workday.end.setTime(bigDayOffset);
                if (state.workday.workdayExtended !== undefined) {
                    state.workday.workdayExtended += state.config.bigDayBonus;
                } else {
                    state.workday.workdayExtended = state.config.bigDayBonus;
                }
                state.cycle.bigDayCount += 1;
                state.day.bigDayExemptionUsed = true;
                state.workday.is16HourExemptionApplied = true;

                if (!state.customCodes.disableRemoveOrTrimViolationsAfter16hException) {
                    removeOrTrimViolationPeriods(state, state.workday.end, "Workday");
                }

                return;
            }
        }
    },
    applyBigDay = (state: IState) => {
        if (state.customCodes.disableExemptionLogsInteractionRefactor) {
            applyBigDayOld(state);
            return;
        }

        if (!state.workday.end || !state.workday.hasStarted || !state.config.bigDayBonus) {
            return;
        }

        if (state.exemption16HLogs.length > 0 && state.exemption16HLogs[0].dateTime.getTime() > state.nextDate) {
            return;
        }

        // Try and get valid 16 hour exemption for current workday.
        while (
            state.exemption16HLogs.length > 0 &&
            state.exemption16HLogs[0].dateTime.getTime() <= state.workday.end.getTime() + state.config.bigDayBonus
        ) {
            let exemption = state.exemption16HLogs.shift();
            let exemptionDate = exemption.dateTime;
            if (isExemptionAppliedDuringWorkday(state, exemptionDate) && isBigDayExemptionReset(state)) {
                if (state.workday.is16HourExemptionApplied) {
                    continue;
                }

                const extensionParams = {
                    hasAdverse: state.workday.isAdverseLogApplied,
                    hasBigDay: true,
                    adverseDoesNotExtendWorkday: state.config.adverseDoesNotExtendWorkday,
                    bigDayExtensionAmount: state.config.bigDayBonus
                };
                const extensions = calculateExemptionExtensions(extensionParams);
                applyWorkdayExtensions(state, extensions.workday);

                // Indicate bigDay exemption was used even if no extension was applied
                state.cycle.bigDayCount += 1;
                state.day.bigDayExemptionUsed = true;
                state.workday.is16HourExemptionApplied = true;

                if (!state.customCodes.disableRemoveOrTrimViolationsAfter16hException) {
                    removeOrTrimViolationPeriods(state, state.workday.end, "Workday");
                }

                return;
            }
        }
    },
    isLogInsideCurrentWorkday = (state: IState, logDateTime: Date) =>
        shouldApplyExemptionThisIteration(state, logDateTime),
    /**
     * Determines if an exemption log (Adverse, Railroad) should be applied during the current iteration.
     * Uses strict comparison (<) for non-final iterations to prevent exemptions from applying to the wrong workday.
     * Uses inclusive comparison (<=) on the final iteration to ensure exemptions at exact boundary timestamps are not skipped.
     */
    shouldApplyExemptionThisIteration = (state: IState, exemptionLogDateTime: Date) => {
        let isAfterStartOfWorkday = !!state.workday.start && state.workday.start.getTime() <= exemptionLogDateTime.getTime();
        let isBeforeCurrentDate = exemptionLogDateTime.getTime() < state.currentDate.getTime();
        let isLastIteration = state.current === state.next;
        let isBeforeNextLog = isLastIteration
            ? exemptionLogDateTime.getTime() <= state.next.dateTime.getTime()
            : exemptionLogDateTime.getTime() < state.next.dateTime.getTime();

        return isBeforeNextLog && (isAfterStartOfWorkday || isBeforeCurrentDate);
    },
    applyAdverseLogOld = (state: IState) => {
        if (state.adverseLogs.length === 0 || !state.config.workday || state.workday.drivingLimit === undefined) {
            return;
        }
        // adverse log will be applied to the workday in between workday resets
        while (state.adverseLogs.length !== 0 && isLogInsideCurrentWorkday(state, state.adverseLogs[0].dateTime)) {
            let adcLog = state.adverseLogs.shift();
            state.workday.adverseLogStart = adcLog.dateTime;
            state.workday.isAdverseLogApplied = true;
            if (state.workday.drivingExtended === undefined) {
                state.workday.drivingLimit += adverseExtensionAmount;
                state.workday.drivingExtended = adverseExtensionAmount;
                if (state.config.adverseDoesNotExtendWorkday !== true) {
                    if (state.config.bigDayBonus && state.workday.is16HourExemptionApplied) {
                        state.workday.end?.setTime(state.workday.end.getTime() - state.config.bigDayBonus);
                        state.workday.is16HourExemptionApplied = false;
                        state.day.bigDayExemptionUsed = false;
                    }
                    state.workday.workdayExtended = (state.workday.workdayExtended || 0) + adverseExtensionAmount;
                    state.workday.end?.setTime(state.workday.end.getTime() + adverseExtensionAmount);
                }
                if (state.workday.dutyLimit !== undefined) {
                    state.workday.dutyLimit += adverseExtensionAmount;
                }
                state.day.isAdverseLogApplied = true;
                calculateWorkdayDrivingViolation(state);
            }
        }
    },
    applyAdverseLog = (state: IState) => {
        if (state.customCodes.disableExemptionLogsInteractionRefactor) {
            applyAdverseLogOld(state);
            return;
        }

        if (state.adverseLogs.length === 0 || !state.config.workday || state.workday.isAdverseLogApplied) {
            return;
        }
        // adverse log will be applied to the workday in between workday resets
        while (state.adverseLogs.length !== 0 && isLogInsideCurrentWorkday(state, state.adverseLogs[0].dateTime)) {
            let adcLog = state.adverseLogs.shift();
            state.workday.adverseLogStart = adcLog.dateTime;

            if (state.workday.isAdverseLogApplied) {
                continue;
            }

            const extensionParams = {
                hasAdverse: true,
                hasBigDay: state.workday.is16HourExemptionApplied,
                adverseDoesNotExtendWorkday: state.config.adverseDoesNotExtendWorkday,
                bigDayExtensionAmount: state.config.bigDayBonus
            };
            const extensions = calculateExemptionExtensions(extensionParams);
            applyDrivingExtensions(state, extensions.driving);
            applyDutyExtensions(state, extensions.duty);
            applyWorkdayExtensions(state, extensions.workday);

            state.workday.isAdverseLogApplied = true;
            state.day.isAdverseLogApplied = true;
            calculateWorkdayDrivingViolation(state);
        }
    },
    applyRailroadLog = (state: IState) => {
        if (state.railroadLogs.length === 0 || !state.config.workday) {
            return;
        }
        // Railroad log will be applied to the workday in between workday resets
        while (state.railroadLogs.length !== 0 && isLogInsideCurrentWorkday(state, state.railroadLogs[0].dateTime)) {
            state.railroadLogs.shift();
            state.day.isRailroadLogApplied = true;
            state.cycle.cycleMaximum += railroadCycleExtensionAmount;
            state.cycle.toDate?.setTime(state.cycle.toDate.getTime() + railroadCycleExtensionAmount);
            if (state.workday !== undefined) {
                state.workday.workdayExtended = (state.workday.workdayExtended || 0) + railroadWorkdayExtensionAmount;
                state.workday.end?.setTime(state.workday.end.getTime() + railroadWorkdayExtensionAmount);
                calculateWorkdayDrivingViolation(state);
            }
        }
    },
    addDrivingPeriod = (newPeriod: IPeriod, drivingPeriods: IPeriod[]) => {
        for (const period of drivingPeriods) {
            const isDuplicate = period.fromDate.getTime() === newPeriod.fromDate.getTime();
            if (isDuplicate) {
                period.toDate = new Date(Math.max(newPeriod.toDate.getTime(), period.toDate.getTime()));
                return;
            }
        }

        drivingPeriods.push(newPeriod);
    },
    startViolation = (
        state: IState,
        id: string | null | undefined,
        type: TViolationType,
        fromDate: Date,
        toDate: Date,
        extended?: number
    ) => {
        // Handles the window only when an Emergency Response exemption affects it.
        if (
            tryRecordEmergencyResponseAdjustedViolation(state, type, fromDate, toDate, (rangeFromDate, rangeToDate) =>
                recordViolationRange(state, id, type, rangeFromDate, rangeToDate, extended)
            )
        ) {
            return;
        }

        recordViolationRange(state, id, type, fromDate, toDate, extended);
    },
    recordViolationRange = (
        state: IState,
        id: string | null | undefined,
        type: TViolationType,
        fromDate: Date,
        toDate: Date,
        extended?: number
    ) => {
        if (state.logs.length === 0 || state.pastViolations.some(v => v.type === type && v.fromDate.getTime() === fromDate.getTime())) {
            return;
        }
        // eslint-disable-next-line complexity
        state.rulesetDateRanges.forEach(ruleSetDateRange => {
            if (
                (!ruleSetDateRange.end || fromDate < ruleSetDateRange.end) &&
                (!toDate || !ruleSetDateRange.start || toDate > ruleSetDateRange.start)
            ) {
                let currentViolation: IRawViolation | undefined = state.currentViolations[type] || {
                    drivingDuration: msToTimespan(0),
                    logId: id,
                    reason: "", // TODO: populate this here?
                    fromDate: fromDate,
                    type: type
                };
                state.currentViolations[type] = currentViolation;
                if (state.config.workday && hasPartialSplitForCurrentWorkdaySymmetric(state)) {
                    if (state.config.workday.workdayMaximum && state.config.workday.workdayMaximumWhenSplitSB) {
                        currentViolation.hoursLimit = (state.config.workday.workdayMaximumWhenSplitSB - state.config.workday.workdayMaximum) / HOURS;
                    }
                }
                if (extended && !currentViolation.hoursLimit) {
                    if (currentViolation.fromDate < fromDate) {
                        // workday was extended after driver was already in violation
                        currentViolation.fromDate = fromDate;
                    }
                    currentViolation.hoursLimit = extended;
                }
                let adjustedFromDate = fromDate;
                if (ruleSetDateRange.start && fromDate < ruleSetDateRange.start) {
                    // trim start of violation to start
                    adjustedFromDate = ruleSetDateRange.start;
                    currentViolation.fromDate = adjustedFromDate;
                }
                let adjustedToDate = toDate;
                if (ruleSetDateRange.end && (!toDate || toDate > ruleSetDateRange.end)) {
                    adjustedToDate = ruleSetDateRange.end;
                    currentViolation.toDate = ruleSetDateRange.end;
                    delete state.currentViolations[type];
                    state.pastViolations.push(currentViolation);
                }
                if (state.customCodes.disableRemoveOrTrimViolationsAfter16hException) {
                    updateDrivingDurationAndLastDriveDateRangeOld(currentViolation, adjustedFromDate, adjustedToDate);
                } else {
                    if (!currentViolation.drivingPeriods) {
                        currentViolation.drivingPeriods = [];
                    }
                    addDrivingPeriod({ fromDate: new Date(adjustedFromDate), toDate: new Date(adjustedToDate) }, currentViolation.drivingPeriods);
                    updateFromDateAndDrivingDuration(currentViolation);
                }
            }
        });
    },
    updateFromDateAndDrivingDuration = (violation: IRawViolation): void => {
        if (!violation.drivingPeriods || violation.drivingPeriods.length === 0) {
            return;
        }
        violation.fromDate = new Date(Math.min(...violation.drivingPeriods.map(period => period.fromDate.getTime())));
        const drivingDurationMs = violation.drivingPeriods
            .map(period => period.toDate.getTime() - period.fromDate.getTime())
            .reduce((totalDuration, periodDuration) => totalDuration + periodDuration, 0);
        violation.drivingDuration = msToTimespan(drivingDurationMs);
    },
    updateDrivingDurationAndLastDriveDateRangeOld = (violation: IRawViolation, driveFromDate: Date, driveToDate: Date): void => {
        let uncountedDriveFromDate =
            violation.lastDriveToDate === undefined
                ? driveFromDate
                : new Date(Math.max(driveFromDate.getTime(), violation.lastDriveToDate.getTime()));
        violation.drivingDuration = msToTimespan(
            timeSpanToMs(violation.drivingDuration) + (driveToDate.getTime() - uncountedDriveFromDate.getTime())
        );
        violation.lastDriveToDate = new Date(driveToDate);
    },
    completeViolations = (state: IState, type: TViolationType, dateTime: Date) => {
        // MYG-139401: every genuine recovery — availability returning to zero, and the workday
        // reset in startNewWorkday — reaches the driver's violations through here, so this is
        // where the Emergency Response "still past the limit" marker is retired. The exemption's
        // own two closure paths re-mark straight after calling this; see emergencyResponse.ts.
        clearEmergencyResponseSuppressedViolation(state, type);
        if (state.currentViolations[type]) {
            let violation = state.currentViolations[type];
            delete state.currentViolations[type];
            let rulesetRangeEnd: Date | null = null;
            for (let i = state.rulesetDateRanges.length - 1; i >= 0; i--) {
                let currentRuleset = state.rulesetDateRanges[i];
                if (state.customCodes.disableOngoingViolationRulesetFix) {
                    // MYG-92956 Old logic: Requires currentRuleset.end to be defined
                    if (
                        currentRuleset.start &&
                        currentRuleset.end &&
                        currentRuleset.end >= violation.fromDate &&
                        currentRuleset.start <= violation.fromDate
                    ) {
                        rulesetRangeEnd = currentRuleset.end;
                        break;
                    }
                } else {
                    // MYG-92956 New logic: Allows currentRuleset.end to be undefined for ongoing rulesets
                    if (
                        currentRuleset.start &&
                        currentRuleset.start <= violation.fromDate &&
                        (!currentRuleset.end || currentRuleset.end >= violation.fromDate)
                    ) {
                        rulesetRangeEnd = currentRuleset.end;
                        break;
                    }
                }
            }
            violation.toDate = rulesetRangeEnd ? new Date(Math.min(dateTime.getTime(), rulesetRangeEnd.getTime())) : dateTime;
            state.pastViolations.push(violation);
        }
    },
    createDailyViolation = (state: IState, type: TViolationType, day: IDay) => {
        let getTimeLeftover = (): number => {
            let duration = 0;
            let remainingDailyRestRequired = getRemainingDailyRestRequired(day, state.config);
            if (type === "DailyRest" && remainingDailyRestRequired && day.restCount) {
                duration = remainingDailyRestRequired;
            } else {
                duration = getRemainingDailyOffDutyRequired(day, state.config) ?? 0;
            }
            return duration;
        };
        let fromDate = getBeginningOfDay(day.date, state.toLocalTime, state.toUtcTime);
        let toDate = getBeginningOfNextDay(day.date, state.toLocalTime, state.toUtcTime);
        let pointOfNoReturnDate = new Date(toDate.getTime() - getTimeLeftover());
        if (isDailyViolationExemptedByEmergencyResponse(state, pointOfNoReturnDate)) {
            return;
        }
        state.rulesetDateRanges.forEach(ruleSetDateRange => {
            // the latest ruleset won't have an end date, so use a suitable date in the future
            let ruleSetEnd =
                ruleSetDateRange.end ??
                new Date(1000 + Math.max(toDate.getTime(), state.logs?.[state.logs.length - 1]?.dateTime?.getTime() ?? 0, new Date().getTime()));
            if (!ruleSetDateRange.start || doPeriodsOverlap(ruleSetDateRange.start, ruleSetEnd, pointOfNoReturnDate, toDate)) {
                state.pastViolations.push({
                    type: type,
                    fromDate: fromDate,
                    toDate: new Date(toDate.getTime() - 1000),
                    reason: "",
                    drivingDuration: msToTimespan(0)
                });
            }
        });
    },
    calculateCycleRestViolation = (state: IState) => {
        let cycleRestAvailability = discreteAvailabilityFunctions.CycleRest(state);
        if (cycleRestAvailability === undefined) {
            return;
        }
        if (cycleRestAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "CycleRest",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + cycleRestAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "CycleRest", state.currentDate);
        }
    },
    calculateCycleEndViolation = (state: IState) => {
        let cycleEndAvailability = discreteAvailabilityFunctions.CycleEnd(state);
        if (cycleEndAvailability === undefined) {
            return;
        }
        if (cycleEndAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "CycleEnd",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + cycleEndAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "CycleEnd", state.currentDate);
        }
    },
    calculateCycleViolation = (state: IState) => {
        //  395.3 (b) Cycle duty violation
        let cycleAvailability = discreteAvailabilityFunctions.Cycle(state);
        if (cycleAvailability === undefined) {
            return;
        }
        if (cycleAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "Cycle",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + cycleAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "Cycle", state.currentDate);
        }
    },
    calculateCycleDrivingViolation = (state: IState) => {
        let cycleDrivingAvailability = discreteAvailabilityFunctions.CycleDriving(state);
        if (cycleDrivingAvailability === undefined) {
            return;
        }
        if (cycleDrivingAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "CycleDriving",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + cycleDrivingAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "CycleDriving", state.currentDate);
        }
    },
    calculateDailyDrivingViolation = (state: IState) => {
        const violationType: TViolationType = "DailyDriving";
        let hasGoneBackInTime = state.nextDateIndex !== state.day.dateIndex;
        let dailyDrivingLimit = getDailyDrivingLimit(state.day, state.config);
        if (hasGoneBackInTime || dailyDrivingLimit === undefined || state.current?.status !== "D") {
            return;
        }
        let logDurationSpansMultipleDays = state.currentDateIndex !== state.nextDateIndex;
        if (logDurationSpansMultipleDays) {
            calculateDailyViolationsSpanningMultipleDays(
                state,
                violationType,
                day => getDailyDrivingLimit(day, state.config),
                getDailyDrivingAvailabilityForDay
            );
        } else {
            calculateDailyViolationForCurrentDay(state, violationType, getDailyDrivingAvailabilityForDay);
        }
    },
    calculateDailyViolationForCurrentDay = (
        state: IState,
        violationType: TViolationType,
        getDailyAvailability: (day: IDay, config: IHosRulesetConfig) => number | undefined
    ) => {
        let availability = getDailyAvailability(state.day, state.config);
        if (availability === undefined) {
            return;
        }
        if (availability < 0) {
            let fromDate = new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + availability));
            let toDate = getBeginningOfNextDay(fromDate, state.toLocalTime, state.toUtcTime);
            startViolation(state, state.current.id, violationType, fromDate, toDate);
            completeViolations(state, violationType, toDate);
        }
    },
    // if in D status for multiple days, need to add daily violations for each day.
    calculateDailyViolationsSpanningMultipleDays = (
        state: IState,
        violationType: TViolationType,
        getLimit: (day: IDay) => number | undefined,
        getDailyAvailability: (day: IDay, config: IHosRulesetConfig) => number | undefined
    ) => {
        // first calculate for current day. start will be now - availability, end is end of day.
        calculateDailyViolationForCurrentDay(state, violationType, getDailyAvailability);

        // then calculate for all the days inbetween
        let day;
        for (let i = state.days.length - 2; i >= 0; i--) {
            // if the D log spans the whole day, start time is the start of day plus the limit.
            day = state.days[i];
            let isFirstDay = day.dateIndex === state.currentDateIndex;
            if (isFirstDay) {
                break;
            }
            var limit = getLimit(day);
            if (limit === undefined) {
                return;
            }
            let fromDate = new Date(day.date.getTime() + limit);
            let toDate = getBeginningOfNextDay(day.date, state.toLocalTime, state.toUtcTime);
            startViolation(state, state.current.id, violationType, fromDate, toDate);
            completeViolations(state, violationType, toDate);
        }

        // lastly calculate for the first day (the day the D log starts on). start date is when they first go over the limit. end is midnight
        if (day === undefined) {
            return;
        }
        let availability = getDailyAvailability(day, state.config);
        if (availability === undefined) {
            return;
        }
        if (availability < 0) {
            let toDate = getBeginningOfNextDay(day.date, state.toLocalTime, state.toUtcTime);
            let fromDate = new Date(Math.max(state.currentDate.getTime(), toDate.getTime() + availability));
            startViolation(state, state.current.id, violationType, fromDate, toDate);
            completeViolations(state, violationType, toDate);
        }
    },
    calculateWorkdayDrivingViolation = (state: IState) => {
        // 395.3 (a)(3)(i) Driving violation
        if (state.config.workday && state.workday.drivingLimit !== undefined && state.workday.driveCount !== undefined) {
            let drivingAvailability = discreteAvailabilityFunctions.Driving(state);
            if (drivingAvailability === undefined) {
                return;
            }
            if (drivingAvailability < 0) {
                if (state.current.status === "D") {
                    startViolation(
                        state,
                        state.current.id,
                        "Driving",
                        new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + drivingAvailability)),
                        state.nextDate
                    );
                }
            } else {
                if (state.workday.hasStarted) {
                    delete state.currentViolations["Driving"];
                }
                completeViolations(state, "Driving", state.currentDate);
            }
        }
    },
    calculateDailyDutyViolation = (state: IState) => {
        const violationType: TViolationType = "DailyDuty";
        let hasGoneBackInTime = state.nextDateIndex !== state.day.dateIndex;
        if (!state.isInit || hasGoneBackInTime || state.config.daily?.dutyMaximum === undefined || state.current?.status !== "D") {
            return;
        }
        let logDurationSpansMultipleDays = state.currentDateIndex !== state.nextDateIndex;
        if (logDurationSpansMultipleDays) {
            calculateDailyViolationsSpanningMultipleDays(
                state,
                violationType,
                day => getDailyDutyLimit(day, state.config),
                getDailyDutyAvailabilityForDay
            );
        } else {
            calculateDailyViolationForCurrentDay(state, violationType, getDailyDutyAvailabilityForDay);
        }
    },
    calculateDutySinceCycleRestViolation = (state: IState) => {
        let dutyAvailability = discreteAvailabilityFunctions.DutySinceCycleRest(state);
        if (dutyAvailability === undefined) {
            return;
        }
        if (dutyAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "DutySinceCycleRest",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + dutyAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "DutySinceCycleRest", state.currentDate);
        }
    },
    calculateWorkdayDutyViolation = (state: IState) => {
        let dutyAvailability = discreteAvailabilityFunctions.Duty(state);
        if (dutyAvailability === undefined) {
            return;
        }
        if (dutyAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "Duty",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + dutyAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "Duty", state.currentDate);
        }
    },
    calculateWorkdayViolation = (state: IState) => {
        // 395.3 (a)(2) Workday violation
        if (!state.config.workday) {
            return;
        }
        const workdayMaximum = getConfigWorkdayMaximum(state);
        if (workdayMaximum && state.workday.end) {
            applyBigDay(state);
            applyAdverseLog(state);
            applyRailroadLog(state);
            if (hasPartialSplitForCurrentWorkdaySymmetric(state) && !state.workday.workdayExtended && state.config.workday.workdayMaximumWhenSplitSB) {
                // Use workdayExtended to calculate workdayMaximum when in split SB (for Canada north of 60)
                // Ex: workdayMaximum can be 18h (when in SplitSB) while config workdayMaximum is 20h
                const diff = workdayMaximum - (state.config.workday.workdayMaximum ?? workdayMaximum);
                if (state.workday.workdayExtended !== undefined) {
                    state.workday.workdayExtended += diff;
                } else {
                    state.workday.workdayExtended = diff;
                }
            }
            if (isWorkdayExceeded(state)) {
                // If you are or were driving beyond the workday end date, and you're not currently in a workday violation
                // Violation start is maximum of either current driving time, or when the workday ended (in the case of drive status
                // duration overlapping the workdayEnd)
                if (state.current.status === "D") {
                    let extended: number | undefined;
                    if (state.config.bigDayBonus && state.workday.workdayExtended) {
                        extended = (workdayMaximum + state.workday.workdayExtended) / HOURS;
                    }
                    startViolation(
                        state,
                        state.current.id,
                        "Workday",
                        new Date(Math.max(state.currentDate.getTime(), state.workday.end.getTime())),
                        state.nextDate,
                        extended
                    );
                }
            } else {
                if (state.workday.hasStarted) {
                    // remove invalid workday exemption.
                    // this can happen if the 16 hour exemption is applied after the driver has already gone past the limit.
                    delete state.currentViolations["Workday"];
                }
                completeViolations(state, "Workday", state.currentDate);
            }
        }
    },
    calculateWorkdayRestViolation = (state: IState) => {
        // 395.3 (a)(3)(ii) Rest violation
        if (!state.config.rest) {
            return;
        }
        let restAvailability = discreteAvailabilityFunctions.Rest(state);
        if (restAvailability === undefined) {
            return;
        }
        if (restAvailability < 0) {
            if (state.current.status === "D") {
                startViolation(
                    state,
                    state.current.id,
                    "Rest",
                    new Date(Math.max(state.currentDate.getTime(), state.nextDate.getTime() + restAvailability)),
                    state.nextDate
                );
            }
        } else {
            completeViolations(state, "Rest", state.currentDate);
        }
    },
    calculateViolations = (state: IState) => {
        if (!state.current) {
            return;
        }

        const isReplayingOldLogs = state.workday?.completedSplitSB && state.allocatedCountIndex > state.i;

        if (!isReplayingOldLogs) {
            calculateCycleViolation(state);
            calculateCycleDrivingViolation(state);
            calculateCycleRestViolation(state);
            calculateCycleEndViolation(state);
            calculateDailyDrivingViolation(state);
            calculateDailyDutyViolation(state);
            calculateDutySinceCycleRestViolation(state);
        }

        calculateWorkdayDrivingViolation(state);
        calculateWorkdayDutyViolation(state);
        calculateWorkdayViolation(state);
        calculateWorkdayRestViolation(state);
    },
    calculateDailyRequirements = (state: IState) => {
        let restBreakNeeded: number | undefined = undefined;
        let offDutyNeeded: number | undefined = undefined;
        let remainingDailyRestRequired = getRemainingDailyRestRequired(state.day, state.config);
        if (state.isInit && remainingDailyRestRequired !== undefined) {
            restBreakNeeded = remainingDailyRestRequired;
        }
        let remainingDailyOffDutyRequired = getRemainingDailyOffDutyRequired(state.day, state.config);
        if (state.isInit && remainingDailyOffDutyRequired !== undefined) {
            offDutyNeeded = Math.max(remainingDailyOffDutyRequired, state.day.deferralStatus === "DayTwo" ? getWorkdayOffNeeded(state) : 0);
        }
        return {
            restBreakNeeded: restBreakNeeded !== undefined ? msToTimespan(restBreakNeeded) : undefined,
            offDutyNeeded: offDutyNeeded !== undefined ? msToTimespan(offDutyNeeded) : undefined
        };
    },
    isDailyOffViolation = (state: IState, day: IDay): boolean => {
        const remainingDailyOffDutyRequired = getRemainingDailyOffDutyRequired(day, state.config, true);
        if (state.day.date === day.date) {
            let timeLeftInDay = getTimeLeftInDay(state);
            return (
                day.unCountedOff !== undefined &&
                remainingDailyOffDutyRequired !== undefined &&
                day.unCountedOff + timeLeftInDay < remainingDailyOffDutyRequired
            );
        }
        const getUnneededRestAmountForDay = (): number => {
            // If using deferral and there is extra rest, subtract it from offDutyCount to ensure that deferral does not come from the mandatory 8h.
            // MYG-95946: Only account for qualifying breaks (>= 30 min) when calculating extra rest.
            // The issue is that restCount may be capped at a different value than offDutyCount
            // (e.g., restCount is capped at 8h via countDailyRestBreaks, while offDutyCount
            // includes all qualifying off-duty). This can cause extraRest to be inflated
            // and create false violations.
            if (day.deferralStatus !== "DayOne") {
                return 0;
            }

            const remainingDailyRestRequired = getRemainingDailyRestRequired(day, state.config, true) ?? 0;
            const extraRest = Math.max(0, -remainingDailyRestRequired);

            if (state.customCodes.disableDeferralRestSubtractionFix) {
                return extraRest;
            }

            // MYG-95946 fix: When driver has excess off-duty above the requirement (buffer > 0),
            // cap extraRest at the buffer to prevent false violations.
            // When buffer = 0 (driver exactly meets or doesn't meet requirement), keep original behavior.
            const offDutyCount = day.offDutyCount ?? 0;
            const totalDailyOffDutyRequired = getTotalDailyOffDutyRequired(day, state.config) ?? 0;
            const buffer = offDutyCount - totalDailyOffDutyRequired;

            if (buffer > 0) {
                // Driver has excess off-duty, cap extraRest to prevent false violation
                return Math.min(extraRest, buffer);
            }

            // Driver doesn't have excess off-duty, keep original behavior
            return extraRest;
        };
        return remainingDailyOffDutyRequired !== undefined && remainingDailyOffDutyRequired + getUnneededRestAmountForDay() > 0;
    },
    isDailyRestViolation = (state: IState, day: IDay): boolean => {
        if (day.restCount === undefined) {
            return false;
        }
        let remainingDailyRestRequired = getRemainingDailyRestRequired(day, state.config, true);
        if (state.day.date === day.date) {
            let timeLeftInDay = getTimeLeftInDay(state);
            return (
                day.unCountedOff !== undefined &&
                remainingDailyRestRequired !== undefined &&
                day.unCountedOff + timeLeftInDay < remainingDailyRestRequired
            );
        }
        return remainingDailyRestRequired !== undefined && remainingDailyRestRequired > 0;
    },
    calculateDailyViolations = (state: IState, fromDate?: Date) => {
        if (!state.config.daily) {
            return;
        }
        // Performance optimization: don't check days that have already been checked for the same violation type.
        const datesWithDailyOffViolation = new Set<string>();
        const datesWithDailyRestViolation = new Set<string>();
        state.pastViolations.forEach(violation => {
            violation.type === "DailyOff" && datesWithDailyOffViolation.add(getDateIndex(violation.fromDate, state.toLocalTime));
            violation.type === "DailyRest" && datesWithDailyRestViolation.add(getDateIndex(violation.fromDate, state.toLocalTime));
        });
        let daysAfterFromDate = fromDate ? state.days.filter(d => d.date.getTime() >= fromDate.getTime()) : state.days;
        daysAfterFromDate.forEach(d => {
            !datesWithDailyOffViolation.has(d.dateIndex) && isDailyOffViolation(state, d) && createDailyViolation(state, "DailyOff", d);
            !datesWithDailyRestViolation.has(d.dateIndex) && isDailyRestViolation(state, d) && createDailyViolation(state, "DailyRest", d);
        });
    },
    getDaySummaries = (state: IState): IDaySummary[] => {
        return state.days.map((day: IDay): IDaySummary => {
            let deferralInfo: IDeferralInfo | undefined = undefined;
            if (!state.customCodes.enableAutoCreateNoneDeferralLog) {
                if (day.deferralStatus && day.deferralLogNeeded !== undefined && day.deferralTime !== undefined) {
                    deferralInfo = { time: msToTimespan(day.deferralTime), status: day.deferralStatus, deferralLogRequired: day.deferralLogNeeded };
                }
            } else if (day.deferralStatus && day.deferralTime !== undefined) {
                deferralInfo = {
                    time: msToTimespan(day.deferralTime),
                    status: day.deferralStatus,
                    requiredDeferralLogStatus: day.deferralLogRequired
                };
            }
            return {
                dateTime: state.toLocalTime(day.date),
                onTotal: day.statusCounts["ON"] ? msToTimespan(day.statusCounts["ON"]) : undefined,
                offTotal: day.statusCounts["OFF"] ? msToTimespan(day.statusCounts["OFF"]) : undefined,
                driveTotal: day.statusCounts["D"] ? msToTimespan(day.statusCounts["D"]) : undefined,
                sleeperBerthTotal: day.statusCounts["SB"] ? msToTimespan(day.statusCounts["SB"]) : undefined,
                personalConveyanceTotal: day.statusCounts["PC"] ? msToTimespan(day.statusCounts["PC"]) : undefined,
                yardMoveTotal: day.statusCounts["YM"] ? msToTimespan(day.statusCounts["YM"]) : undefined,
                waitTimeTotal: day.statusCounts["WT"] ? msToTimespan(day.statusCounts["WT"]) : undefined,
                restInfos: day.restInfo ? day.restInfo.map(info => ({ fromDate: info.fromDate, toDate: info.toDate })) : day.restInfo,
                deferralInfo: deferralInfo,
                hoursInCycle: msToTimespan(day.hoursInCycle)
            };
        });
    },
    getWorkdaySummaries = (state: IState): IWorkdaySummary[] => {
        return state.workdays.map((workday: IWorkday): IWorkdaySummary => {
            let workdaySummaryResult: IWorkdaySummary = {
                fromDate: workday.start,
                toDate: workday.end,
                driveExtension: workday.drivingExtended ? msToTimespan(workday.drivingExtended) : undefined,
                workdayExtension: workday.workdayExtended ? msToTimespan(workday.workdayExtended, true) : undefined,
                driveCount: workday.driveCount ? msToTimespan(workday.driveCount) : undefined,
                onDutyCount: workday.onDutyCount ? msToTimespan(workday.onDutyCount) : undefined
            };

            if (workday.resetInfo) {
                workdaySummaryResult.resetInfo = workday.resetInfo;
            }

            const realWorkdayEnd = workday.resetInfo?.eligibleForResetDate;
            if (!realWorkdayEnd || !workday.start) {
                workdaySummaryResult.isInProgress = true;
                return workdaySummaryResult;
            }

            workdaySummaryResult.onDutyIntervals = dutyStatusTracker
                .getAllIntervals({ start: workday.start.getTime(), end: realWorkdayEnd.getTime() })
                .map(interval => ({ start: new Date(interval.start), end: new Date(interval.end) }));

            return workdaySummaryResult;
        });
    },
    getCycleSummaries = (state: IState): ICycleSummary[] => {
        const startedCycles = state.cycles.filter((cycle: ICycle): boolean => cycle.hasStarted);
        const lastCycle = state.cycles[state.cycles.length - 1];
        const hasTrailingUnstartedCycle = lastCycle && !lastCycle.hasStarted && startedCycles.length > 0;

        return startedCycles.map((cycle: ICycle, index: number): ICycleSummary => {
            let restCount = cycle.cycleConsecutiveOffCount;
            // If this is the last started cycle and there's a trailing unstarted cycle,
            // add the unstarted cycle's rest to this cycle's rest so the user sees
            // the full accumulated rest duration (not a frozen/stale value).
            if (!state.customCodes.disablePrevCycleRestDurationCountingFix && hasTrailingUnstartedCycle && index === startedCycles.length - 1) {
                restCount += lastCycle.cycleConsecutiveOffCount;
            }
            return {
                dateTime: cycle.fromDate,
                consecutiveRest: msToTimespan(restCount),
                cycleResetPeriod: cycle.cycleResetPeriod
            };
        });
    },
    allocateOffAndRestTime = (state: IState, diff: number) => {
        state.day.offDutyCount += diff;
    },
    // mutates the previousState object, parsing known Date strings into Date objects
    parsePreviousStateDates = (previousState: IState) => {
        previousState.cycles = previousState.cycles.map(c => {
            c.toDate = parseDateTime(c.toDate);
            c.fromDate = parseDateTime(c.fromDate);
            c.completedRestPeriodDates = c.completedRestPeriodDates.map(rp => parseDateTime(rp));
            if (c.cycleResetPeriod) {
                c.cycleResetPeriod.fromDate = parseDateTime(c.cycleResetPeriod.fromDate);
                c.cycleResetPeriod.eligibleForResetDate = parseDateTime(c.cycleResetPeriod.eligibleForResetDate);
                c.cycleResetPeriod.toDate = parseDateTime(c.cycleResetPeriod.toDate);
            }
            return c;
        });
        previousState.workdays = previousState.workdays.map(wd => {
            wd.start = parseDateTime(wd.start);
            wd.end = parseDateTime(wd.end);
            wd.endOfLastDLog = parseDateTime(wd.endOfLastDLog);
            wd.endOfLastBreak = parseDateTime(wd.endOfLastBreak);
            if (wd.offDutySplitPeriodFromStandardWorkdayReset) {
                wd.offDutySplitPeriodFromStandardWorkdayReset.fromDate = parseDateTime(wd.offDutySplitPeriodFromStandardWorkdayReset.fromDate);
                wd.offDutySplitPeriodFromStandardWorkdayReset.toDate = parseDateTime(wd.offDutySplitPeriodFromStandardWorkdayReset.toDate);
            }
            wd.carriedResetPairingSleeperStart = parseDateTime(wd.carriedResetPairingSleeperStart);
            wd.splitEligibleConsecutiveOffStartTime = parseDateTime(wd.splitEligibleConsecutiveOffStartTime);
            wd.prevLastReducedRestApplyDate = parseDateTime(wd.prevLastReducedRestApplyDate);
            wd.eligibleReducedRestPeriods = wd.eligibleReducedRestPeriods?.map(p => {
                p.start = parseDateTime(p.start);
                p.end = parseDateTime(p.end);
                return p;
            });
            return wd;
        });
        previousState.days = previousState.days.map(d => {
            d.date = parseDateTime(d.date);
            d.restInfo = d.restInfo?.map(r => {
                r.toDate = parseDateTime(r.toDate);
                r.fromDate = parseDateTime(r.fromDate);
                return r;
            });
            return d;
        });
        previousState.pastViolations = previousState.pastViolations.map(p => {
            p.fromDate = parseDateTime(p.fromDate);
            p.toDate = parseDateTime(p.toDate);
            return p;
        });
        previousState.cycleRestartDatesFromOtherRulesets = previousState.cycleRestartDatesFromOtherRulesets?.map(d => parseDateTime(d));
    },
    separateCurrentFromPastViolations = (previousState: IState): { pastViolations: IRawViolation[]; currentViolations: ICurrentViolationMap } => {
        let pastViolations: IRawViolation[] = [];
        let currentViolations: ICurrentViolationMap = {};
        for (let v of previousState.pastViolations) {
            if (v.toDate !== undefined) {
                pastViolations.push(v);
            } else {
                currentViolations[v.type] = v;
            }
        }
        return { pastViolations, currentViolations };
    },
    getState = (
        toLocalTime: (date: Date) => Date,
        toUtcTime: (date: Date) => Date,
        dutyStatusLogs: any[],
        ruleset: IHosRuleset,
        exemptionLogs: any[],
        rulesetDateRanges: IRuleSetDateRange[],
        prev: IState | undefined,
        cycleRestartDatesFromOtherRulesets: Date[],
        customCodes: ICustomCodeState
    ): IState => {
        // sort in reverse-chronological, so removals from the list are from the end and therefore faster
        let cycleRestartDatesFromPreviousValidations = [...cycleRestartDatesFromOtherRulesets].sort((a, b) => b.getTime() - a.getTime());
        if (prev) {
            parsePreviousStateDates(prev);
            let { pastViolations, currentViolations } = separateCurrentFromPastViolations(prev);

            // put temp rest break back into workday consecutive count
            let prevDay = prev.days[prev.days.length - 1];
            let prevWorkday = prev.workdays[prev.workdays.length - 1];
            if (prevDay && prevDay.restCount !== undefined && prevDay.restInfo && prevDay.restInfo.length > 0) {
                let lastRestInfo = prevDay.restInfo[prevDay.restInfo.length - 1];
                if (lastRestInfo.isTemp) {
                    prevDay.restInfo.pop();
                    let timeTaken = parseDateTime(lastRestInfo.toDate).getTime() - parseDateTime(lastRestInfo.fromDate).getTime();
                    prevDay.restCount -= timeTaken;
                    if (lastRestInfo.isWt) {
                        prevWorkday.consecutiveWtCount += timeTaken;
                    } else {
                        prevWorkday.consecutiveOffCount += timeTaken;
                    }
                }
            }
            return {
                config: ruleset.config,
                i: 0,
                allocatedCountIndex: -1,
                current: prev.current,
                next: prev.next,
                currentDate: prev.currentDate,
                nextDate: prev.nextDate,
                duration: prev.duration,
                currentDateIndex: prev.currentDateIndex,
                nextDateIndex: prev.nextDateIndex,
                currentViolations: currentViolations,
                pastViolations: pastViolations,
                workday: prevWorkday || {
                    hasStarted: false,
                    consecutiveOffCount: 0,
                    consecutiveWtCount: 0,
                    consecutiveRestCount: 0
                },
                day: prevDay || {
                    dateIndex: "",
                    date: new Date(0),
                    driveCount: 0,
                    onDutyCount: 0,
                    offDutyCount: 0,
                    statusCounts: {},
                    hoursInCycle: 0
                },
                cycle: prev.cycles[prev.cycles.length - 1] || {
                    fromDate: new Date(0),
                    dutyCountSince24HourRestPeriod: 0,
                    cycleConsecutiveOffCount: 0,
                    driveCount: 0,
                    onDutyCount: 0,
                    days: 0,
                    bigDayCount: 0,
                    hasStarted: false,
                    pauseCountingCycleOff: false,
                    completedRestPeriodDates: [],
                    cycleMaximum: 0
                },
                workdays: prev.workdays,
                days: prev.days,
                cycles: prev.cycles,
                exemption16HLogs: (prev.exemption16HLogs || []).concat(exemptionLogs.filter(log => log.status === "Exemption16H")),
                adverseLogs: (prev.adverseLogs || []).concat(exemptionLogs.filter(log => log.status === "AdverseWeather")),
                railroadLogs: (prev.railroadLogs || []).concat(exemptionLogs.filter(log => log.status === "RailroadExemption")),
                emergencyResponseLogs: collectEmergencyResponseLogs(exemptionLogs, prev.emergencyResponseLogs),
                emergencyResponsePeriods: [],
                // Carried, not rebuilt: a suppressed violation has a toDate, so
                // separateCurrentFromPastViolations files it under pastViolations and the
                // "still past the limit" signal cannot be recovered from the violation list.
                emergencyResponseSuppressedViolations: prev.emergencyResponseSuppressedViolations || [],
                deferralLogs: getDailyDeferralExemptions(
                    toLocalTime,
                    exemptionLogs,
                    customCodes.enableAutoCreateNoneDeferralLog,
                    prev.deferralLogs
                ),
                reducedRestLogs: (prev.reducedRestLogs || []).concat(exemptionLogs.filter(log => log.status === "ExemptionReducedRest")),
                logs: [...dutyStatusLogs],
                rulesetDateRanges,
                isInit: prev.isInit,
                splitSBDuringReset: prev.splitSBDuringReset,
                toLocalTime,
                toUtcTime,
                propertySplitSubConfig: ruleset.config.splitConfig?.type === "asymmetric" ? ruleset.config.splitConfig : undefined,
                intermediateSplitConfig: getIntermediateSplitConfig(ruleset.config, customCodes),
                cycleRestartDatesFromOtherRulesets: cycleRestartDatesFromPreviousValidations,
                customCodes
            };
        }
        return {
            config: ruleset.config,
            i: 0,
            allocatedCountIndex: -1,
            current: undefined,
            next: undefined,
            currentDate: new Date(0),
            nextDate: new Date(0),
            duration: 0,
            currentDateIndex: "",
            nextDateIndex: "",
            currentViolations: {},
            pastViolations: [],
            workday: {
                hasStarted: false,
                consecutiveOffCount: 0,
                consecutiveWtCount: 0
            },
            day: {
                dateIndex: "",
                date: new Date(0),
                driveCount: 0,
                onDutyCount: 0,
                offDutyCount: 0,
                statusCounts: {},
                hoursInCycle: 0
            },
            cycle: {
                fromDate: new Date(0),
                dutyCountSince24HourRestPeriod: 0,
                cycleConsecutiveOffCount: 0,
                driveCount: 0,
                onDutyCount: 0,
                days: 0,
                bigDayCount: 0,
                hasStarted: false,
                pauseCountingCycleOff: false,
                completedRestPeriodDates: [],
                cycleMaximum: 0
            },
            workdays: [],
            days: [],
            cycles: [],
            exemption16HLogs: exemptionLogs.filter(log => log.status === "Exemption16H"),
            adverseLogs: exemptionLogs.filter(log => log.status === "AdverseWeather"),
            railroadLogs: exemptionLogs.filter(log => log.status === "RailroadExemption"),
            emergencyResponseLogs: collectEmergencyResponseLogs(exemptionLogs),
            emergencyResponsePeriods: [],
            emergencyResponseSuppressedViolations: [],
            deferralLogs: getDailyDeferralExemptions(
                toLocalTime,
                exemptionLogs,
                customCodes.enableAutoCreateNoneDeferralLog
            ),
            reducedRestLogs: exemptionLogs.filter(log => log.status === "ExemptionReducedRest"),
            logs: [...dutyStatusLogs],
            rulesetDateRanges,
            isInit: false,
            splitSBDuringReset: 0,
            toLocalTime,
            toUtcTime,
            propertySplitSubConfig: ruleset.config.splitConfig?.type === "asymmetric" ? ruleset.config.splitConfig : undefined,
            intermediateSplitConfig: getIntermediateSplitConfig(ruleset.config, customCodes),
            cycleRestartDatesFromOtherRulesets: cycleRestartDatesFromPreviousValidations,
            customCodes
        };
    },
    addDummyLog = (state: IState, dummyLogDate: Date | undefined) => {
        if (dummyLogDate === undefined) {
            return;
        }
        // Create a dummy log that is a duplicate of the most recent status log, with the provided dummyLogDate
        let prevLog = state.logs.length ? state.logs[state.logs.length - 1] : state.next;
        if (prevLog) {
            let dummyLog = JSON.parse(JSON.stringify(prevLog));
            dummyLog.dateTime = dummyLogDate;
            dummyLog.dummyLog = true;
            state.logs.push(dummyLog);
        }
    },
    truncateState = (state: IState, fromDate: Date | undefined): any => {
        if (fromDate === undefined) {
            return {
                next: state.next,
                adverseLogs: state.adverseLogs,
                exemption16HLogs: state.exemption16HLogs,
                railroadLogs: state.railroadLogs,
                emergencyResponseLogs: prunedEmergencyResponseLogsFor(state, undefined),
                emergencyResponseSuppressedViolations: state.emergencyResponseSuppressedViolations,
                deferralLogs: state.deferralLogs,
                isInit: state.isInit,
                pastViolations: state.pastViolations,
                cycles: state.cycles,
                workdays: state.workdays,
                days: state.days,
                splitSBDuringReset: state.splitSBDuringReset,
                intermediateSplitConfig: state.intermediateSplitConfig
            };
        }
        let dayDate = getBeginningOfDay(fromDate, state.toLocalTime, state.toUtcTime);
        return {
            next: state.next,
            adverseLogs: state.adverseLogs,
            exemption16HLogs: state.exemption16HLogs,
            railroadLogs: state.railroadLogs,
            emergencyResponseLogs: prunedEmergencyResponseLogsFor(state, dayDate),
            emergencyResponseSuppressedViolations: state.emergencyResponseSuppressedViolations,
            deferralLogs: state.deferralLogs,
            isInit: state.isInit,
            pastViolations: state.pastViolations.filter(v => !v.toDate || v.toDate > fromDate),
            cycles: state.cycles.filter(c => !c.toDate || c.toDate > fromDate),
            workdays: state.workdays.filter(w => !w.end || w.end > fromDate),
            days: state.days.filter(d => !d.date || d.date >= dayDate),
            splitSBDuringReset: state.splitSBDuringReset,
            intermediateSplitConfig: state.intermediateSplitConfig
        };
    },
    allocateRestTimeForFirstDay = (state: IState, beginningOfDay: number, diff: number) => {
        // any time from the start of day until the first log is considered off-duty time
        let totalDailyRestRequired = getTotalDailyRestRequired(state.day, state.config);
        let remainingDailyRestRequired = getRemainingDailyRestRequired(state.day, state.config);
        if (
            totalDailyRestRequired !== undefined &&
            remainingDailyRestRequired !== undefined &&
            state.config.rest &&
            state.day.restCount !== undefined &&
            state.day.restInfo !== undefined
        ) {
            let extraBreakTime = Math.min(remainingDailyRestRequired, diff - totalDailyRestRequired);
            if (extraBreakTime > 0) {
                state.day.restCount += extraBreakTime;
                state.day.restInfo.push({ fromDate: new Date(beginningOfDay), toDate: new Date(beginningOfDay + extraBreakTime) });
            }
        }
    },
    updateCountingCycleConsecutiveOff = (state: IState) => {
        // If processing new logs after split SB is completed, OR if consecutive cycle was reset from an on duty status, THEN start recalculating cycle again
        if (state.cycle.pauseCountingCycleOff) {
            state.cycle.pauseCountingCycleOff = false;
        }
    },
    initializeFirstState = (state: IState) => {
        state.next = state.logs[0];
        state.i = 1;
        let startingDate = new Date(state.logs[0].dateTime);
        let dateIndex = getDateIndex(startingDate, state.toLocalTime);
        tryStartNewDay(state, startingDate, dateIndex);
        canadianDutyDeferral(state, dateIndex);
        state.nextDateIndex = dateIndex;

        // time at beginning of day is considered off time.
        let beginningOfDay = getBeginningOfDay(startingDate, state.toLocalTime, state.toUtcTime).getTime();
        let diff = Math.max(0, startingDate.getTime() - beginningOfDay);
        state.day.statusCounts["OFF"] = diff;
        allocateOffAndRestTime(state, diff);
        allocateRestTimeForFirstDay(state, beginningOfDay, diff);
        startNewCycle(state, startingDate);
        startNewWorkday(state, startingDate, false, undefined);
        state.isInit = true;
    },
    processLogs = (state: IState) => {
        for (; state.i <= state.logs.length; state.i++) {
            state.current = state.next;
            state.next = state.i < state.logs.length ? state.logs[state.i] : state.current;
            state.currentDate = parseDateTime(state.current.dateTime);
            state.nextDate = parseDateTime(state.next.dateTime);
            state.duration = state.nextDate.getTime() - state.currentDate.getTime();
            state.currentDateIndex = state.nextDateIndex ?? getDateIndex(state.currentDate, state.toLocalTime);
            state.nextDateIndex = getDateIndex(state.nextDate, state.toLocalTime);

            // Allocate status counts (OFF,SB,ON,D) on a daily basis
            // If log loop goes back (SB prov.), don't double count, so refer to allocatedCountIndex
            if (state.allocatedCountIndex < state.i) {
                allocateCounts(state);
                state.allocatedCountIndex = state.i;
                updateCountingCycleConsecutiveOff(state);
            }
            if (state.customCodes.disableRemoveOrTrimViolationsAfter16hException) {
                state.current.status !== "D" &&
                    Object.keys(state.currentViolations).forEach(vType => delete state.currentViolations[vType].lastDriveToDate);
            }
            if (offDutyStatuses.indexOf(state.current.status) !== -1) {
                countOffDutyTime(state);
            } else if (onDutyStatuses.indexOf(state.current.status) !== -1) {
                updateCountingCycleConsecutiveOff(state);
                countOnDutyTime(state);
            }
            applyAdverseLog(state);
            applyRailroadLog(state);
            evaluateSplit(state);
            checkIfWorkdayReset(state);
            applyReducedRestAndCalculateWorkdayResetInfo(state);
            extendWorkdayWithWt(state);
            calculateViolations(state);
            closeViolationsInterruptedByEmergencyResponse(state);
        }
    },
    getValidationResult = (
        state: IState,
        userHosRuleSet: IUserHosRuleSet,
        getAllRecaps = false,
        getDiscreteAvailability = false,
        getNextHosEventDateTime = false,
        getNextAvailabilityEvent = false
    ): IValidation => {
        let { availability, preciseAvailability } = calculateAvailability(state, getAllRecaps);
        let validationResult: IValidation = {
            violations: state.pastViolations.map(getCleanViolation),
            availability,
            preciseAvailability,
            daySummaries: getDaySummaries(state),
            workdaySummaries: getWorkdaySummaries(state),
            cycleSummaries: getCycleSummaries(state)
        };
        if (getDiscreteAvailability) {
            validationResult.discreteAvailability = getDiscreteAvailabilitySummary(state);
        }
        if (getNextHosEventDateTime) {
            validationResult.nextHosEventDate = getNextHosEventDate(state);
        }
        if (getNextAvailabilityEvent) {
            validationResult.nextAvailabilityEventDates = getNextAvailabilityEventDates(state);
        }
        // optional validation properties
        let dailyRequirements = calculateDailyRequirements(state);
        if (dailyRequirements.restBreakNeeded) {
            validationResult.restBreakNeeded = dailyRequirements.restBreakNeeded;
        }
        if (dailyRequirements.offDutyNeeded) {
            validationResult.offDutyNeeded = dailyRequirements.offDutyNeeded;
        }
        if (state.config.resetPreviousCycle && state.cycle.onDutyCount !== 0) {
            validationResult.rulesetsThatCannotChange = [userHosRuleSet];
        }
        if (state.next) {
            validationResult.currentDutyStatus = state.next.status;
        }
        let remainingSplitDuration = getRemainingSplitDuration(state);
        if (remainingSplitDuration) {
            validationResult.remainingSplitDuration = remainingSplitDuration;
        }
        attachNonSplitView(state, validationResult);
        return validationResult;
    };

/**
 * MYG-133117: attaches the "without split sleeper benefit" view, projected from the
 * SAME calculation (never a second engine pass). Always emitted for a valid calc —
 * equal to the normal result whenever there is no in-progress split freeze to strip.
 */
const attachNonSplitView = (state: IState, validationResult: IValidation): void => {
        if (state.customCodes.disableNonSplitAvailability) {
            return;
        }
        const nonSplit = computeNonSplitResult(
            validationResult,
            state.config.workday?.workdayMaximum,
            state.workday.workdayExtended ?? 0,
            currentWorkdayDrivingIntervalsOf(state),
            state.workday.earnedSplitExclusion ?? 0,
            isEmergencyResponseActive(state.emergencyResponsePeriods, state.nextDate) ? getTimeLeftInDay(state) : 0
        );
        validationResult.nonSplitAvailability = nonSplit.availability;
        validationResult.nonSplitPreciseAvailability = nonSplit.preciseAvailability;
        validationResult.nonSplitViolations = nonSplit.violations;
    },
    currentWorkdayDrivingIntervalsOf = (state: IState): IDrivingInterval[] => {
        if (!state.workday.start || !state.workday.end || state.logs.length === 0) {
            return [];
        }
        const now = state.currentDate.getTime();
        const drivingTracker = new DutyStatusIntervalTracker(state.logs, ["D"], undefined, undefined, now);
        return drivingTracker
            .getAllIntervals({ start: state.workday.start.getTime(), end: Math.max(state.workday.end.getTime(), now) })
            // MYG-139401: exempt time is removed here rather than in the projection, so that every
            // violation the projection derives inherits the exemption without knowing about it —
            // the same non-exempt ranges the engine's own violation path records against. Returns
            // the interval unchanged when the driver has no exempt periods.
            .reduce<IDrivingInterval[]>((intervals, interval) => {
                getNonExemptEmergencyResponseRanges(state.emergencyResponsePeriods, new Date(interval.start), new Date(interval.end)).forEach(range => {
                    intervals.push({ start: range.start, end: range.end });
                });
                return intervals;
            }, []);
    };

export { startNewWorkday, startViolation, completeViolations };

export function HosCalculator(
    dummyLogDate: Date | undefined,
    dutyStatusLogs: any[],
    ruleset: IHosRuleset,
    exemptionLogs: any[],
    toLocalTime: (date: Date) => Date,
    toUtcTime: (date: Date) => Date,
    userHosRuleSet: IUserHosRuleSet,
    rulesetDateRanges: IRuleSetDateRange[],
    customCodes: ICustomCodeState,
    previousState?: IState,
    fromDate?: Date,
    getAllRecaps = false,
    getDiscreteAvailability = false,
    getNextHosEventDateTime = false,
    cycleRestartDatesFromOtherRulesets: Date[] = [],
    getFullState = false,
    getNextAvailabilityEvent = false
): ICalculationResult {
    let state: IState = getState(
        toLocalTime,
        toUtcTime,
        dutyStatusLogs,
        ruleset,
        exemptionLogs,
        rulesetDateRanges,
        previousState,
        cycleRestartDatesFromOtherRulesets,
        customCodes
    );

    initialiseEmergencyResponse(state);

    dutyStatusTracker = new DutyStatusIntervalTracker(state.logs, onDutyStatuses as THosStatus[]);

    addDummyLog(state, dummyLogDate);
    if (!previousState?.isInit && state.logs.length > 0) {
        initializeFirstState(state);
    }
    if (previousState && state.adverseLogs.length > 0) {
        applyAdverseLog(state);
    }
    if (state.next) {
        processLogs(state);
    }
    if (state.logs.length === 0) {
        applyAdverseLog(state);
        applyRailroadLog(state);
        calculateViolations(state);
    }
    calculateDailyViolations(state, fromDate);

    state.pastViolations = [...state.pastViolations, ...Object.keys(state.currentViolations).map(k => state.currentViolations[k])];
    checkIfUnneededWorkdayResetCausedDailyRestViolationCanadaHos(state);
    return {
        validation: getValidationResult(state, userHosRuleSet, getAllRecaps, getDiscreteAvailability, getNextHosEventDateTime, getNextAvailabilityEvent),
        state: getFullState ? state : truncateState(state, fromDate),
    };
}
